// File: src/pages/CreateOrder.jsx
import React, { useEffect, useState, useContext, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Modal,
  ModalHeader,
  ModalBody,
  Card,
  CardBody,
  Col,
  Form,
  Row,
  Input,
  Label,
  Button,
  Table,
  Spinner,
} from "reactstrap";
import Multiselect from "multiselect-react-dropdown";
import {
  _Get,
  _PostSave,
  _PutUpdate,
  SaveOrder,
} from "../import/ApiEndPoint/ApiCalling";
import {
  EmergencyInvoice,
  viewEmergencyInvoice,
  updateEmergencyInvoice,
} from "../import/ApiEndPoint/Api";
import UserContext from "../import/context/Context";
import { GstCalculation } from "../import/GstCalculation";
import swal from "sweetalert";
import "./InvoiceUI.css";

/* ------------------------------------------------------------------ */
/* ----------------------- Helpers: Rounding & GST ------------------- */
/* ------------------------------------------------------------------ */
const round2 = (n) => Number((Number(n) || 0).toFixed(2));
const floor2 = (n) => Math.floor((Number(n) || 0) * 100) / 100;
const ceil2 = (n) => Math.ceil((Number(n) || 0) * 100) / 100;

const parseGstPct = (g) => {
  if (g === null || g === undefined) return 0;
  if (typeof g === "number") return g || 0;
  const s = String(g).trim();
  if (!s) return 0;
  return s
    .split("+")
    .map((x) => Number(x) || 0)
    .reduce((a, b) => a + b, 0);
};

const num = (v) => (Number.isFinite(+v) ? +v : 0);

const normId = (val) => {
  if (!val) return "";
  if (typeof val === "string") return val;
  if (typeof val === "object") {
    if (val._id) return String(val._id);
    if (val.$oid) return String(val.$oid);
  }
  return String(val);
};

// Keep expected 404 responses silent.
// Your backend uses 404 for normal "no data found" cases, so these should not show
// the React red overlay or SweetAlert popup during page load/background checks.
const isAxios404 = (err) =>
  err?.response?.status === 404 || err?.status === 404;

const silentApiError = (err) => {
  if (!isAxios404(err)) {
    console.error("API Error:", err);
  }
};

const firstDefined = (...vals) => {
  for (const value of vals) {
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return undefined;
};

const parseFYStart = (fy) => {
  const startYear = Number(String(fy || "").split("-")[0]);
  if (Number.isFinite(startYear)) return startYear;

  const today = new Date();
  return today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
};

const getPreviousFY = (fy) => {
  const start = parseFYStart(fy) - 1;
  return `${start}-${String(start + 1).slice(-2)}`;
};

const buildSelectedDatabase = (database, financialYear) =>
  `${database}-${financialYear}`;

const safeJson = (value) => {
  if (!value) return null;
  if (typeof value === "object") return value;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const findFYEntry = (container, financialYear) => {
  const data = safeJson(container);
  if (!data) return null;

  if (Array.isArray(data)) {
    return (
      data.find(
        (item) =>
          String(
            item?.financialYear ||
              item?.FinancialYear ||
              item?.fy ||
              item?.year ||
              "",
          ) === String(financialYear),
      ) || null
    );
  }

  if (typeof data === "object") {
    return (
      data[financialYear] ||
      data[String(financialYear).replace("-", "_")] ||
      data[String(financialYear).replace("-", "/")] ||
      null
    );
  }

  return null;
};

const getOpeningFinancialYear = (row = {}) =>
  firstDefined(
    row?._productDetailsFromProductList?.openingFinancialYear,
    row?._productDetailsFromProductList?.OpeningFinancialYear,
    row?._productDetailsFromProductList?.financialYearOpeningYear,
    row?._productDetailsFromProductList?.openingFY,
    row?._productDetailsFromProductList?.OpeningFY,
    row?._productDetailsFromProductList?.openingYear,
    row?.openingFinancialYear,
    row?.OpeningFinancialYear,
    row?.financialYearOpeningYear,
    row?.productId?.openingFinancialYear,
    row?.productId?.OpeningFinancialYear,
    row?.product?.openingFinancialYear,
    row?.product?.OpeningFinancialYear,
  );

const getProductSavedOpening = (row = {}) => {
  const productDetails =
    row?._productDetailsFromProductList ||
    (row?.productId && typeof row.productId === "object"
      ? row.productId
      : {}) ||
    row?.product ||
    {};

  const qty = firstDefined(
    productDetails?.Opening_Stock,
    productDetails?.openingStock,
    productDetails?.openingQty,
    productDetails?.oQty,
    productDetails?.financialYearOpeningStock,
    productDetails?.OpeningStock,
    row?.Opening_Stock,
    row?.openingStock,
    row?.openingQty,
    row?.oQty,
  );

  const rate = firstDefined(
    productDetails?.openingRate,
    productDetails?.Opening_Rate,
    productDetails?.OpeningRate,
    productDetails?.oRate,
    productDetails?.purchaseRate,
    productDetails?.PurchaseRate,
    productDetails?.Product_Purchase_Rate,
    productDetails?.Purchase_Price,
    productDetails?.purchasePrice,
    productDetails?.PurchasePrice,
    productDetails?.costPrice,
    productDetails?.Product_Rate,
    productDetails?.price,
    row?.openingRate,
    row?.oRate,
    row?.price,
  );

  return {
    qty: num(qty),
    rate: num(rate),
  };
};

const extractBackendFYOpening = (row, financialYear) => {
  const directFY = String(
    row?.financialYear ||
      row?.openingFinancialYear ||
      row?.FinancialYear ||
      row?.fy ||
      "",
  );

  if (directFY && directFY === String(financialYear)) {
    const qty = firstDefined(
      row?.financialYearOpeningStock,
      row?.openingStock,
      row?.openingQty,
      row?.Opening_Stock,
      row?.oQty,
    );
    const rate = firstDefined(
      row?.financialYearOpeningRate,
      row?.openingRate,
      row?.oRate,
    );

    if (qty !== undefined || rate !== undefined) {
      return { qty: num(qty), rate: num(rate) };
    }
  }

  const possibleFYContainers = [
    row?.openingStockByFY,
    row?.openingStocks,
    row?.financialYearOpenings,
    row?.openingByFinancialYear,
    row?.fyOpeningStock,
    row?.yearWiseOpeningStock,
    row?.productId?.openingStockByFY,
    row?.productId?.openingStocks,
    row?.productId?.financialYearOpenings,
    row?.productId?.openingByFinancialYear,
  ];

  for (const container of possibleFYContainers) {
    const entry = findFYEntry(container, financialYear);
    if (!entry) continue;

    return {
      qty: num(
        firstDefined(
          entry?.openingStock,
          entry?.openingQty,
          entry?.Opening_Stock,
          entry?.oQty,
          entry?.qty,
          entry?.closingQty,
        ),
      ),
      rate: num(
        firstDefined(
          entry?.openingRate,
          entry?.rate,
          entry?.closingAveRate,
          entry?.closingRate,
        ),
      ),
    };
  }

  return null;
};

const stockSnapshotKey = (database, financialYear) =>
  `FY_CLOSING_STOCK_SNAPSHOT:${database}:${financialYear}`;

const getProductIdForStockKey = (row = {}) =>
  normId(
    row?._productIdResolved ||
      row?._productDetailsFromProductList?._id ||
      row?._productDetailsFromProductList?.id ||
      row?.productId?._id ||
      row?.productId ||
      row?.pId ||
      row?.ProductId ||
      row?.product?._id ||
      "",
  );

const getWarehouseIdForStockKey = (row = {}) =>
  normId(
    row?._warehouseIdResolved ||
      row?.warehouseId?._id ||
      row?.warehouseId ||
      row?.warehouse?._id ||
      row?.warehouse ||
      row?.warehouseId1 ||
      "",
  );

const getProductTitleForStockKey = (row = {}) =>
  String(
    row?.Product_Title ||
      row?._productDetailsFromProductList?.Product_Title ||
      row?._productDetailsFromProductList?.productName ||
      row?.productId?.Product_Title ||
      row?.productName ||
      row?.ProductName ||
      "",
  )
    .trim()
    .toLowerCase();

const getStockLookupKeys = (row = {}) => {
  const warehouseId = getWarehouseIdForStockKey(row);
  const productId = getProductIdForStockKey(row);
  const productTitle = getProductTitleForStockKey(row);
  const keys = [];

  if (warehouseId && productId) keys.push(`${warehouseId}::${productId}`);
  if (warehouseId && productTitle)
    keys.push(`${warehouseId}::title:${productTitle}`);
  if (productId) keys.push(`id:${productId}`);
  if (productTitle) keys.push(`title:${productTitle}`);

  return [...new Set(keys.filter(Boolean))];
};

const findPreviousClosingForRow = (row, previousClosingMap = {}) => {
  for (const key of getStockLookupKeys(row)) {
    if (previousClosingMap?.[key]) return previousClosingMap[key];
  }
  return null;
};

const addPreviousClosingToMap = (map, row, closing) => {
  const keys = getStockLookupKeys(row);
  if (!keys.length) return;

  const payload = {
    closingQty: num(closing?.closingQty),
    closingAveRate: num(closing?.closingAveRate),
    closingTotal: num(closing?.closingTotal),
  };

  keys.forEach((key) => {
    if (!map[key]) {
      map[key] = { ...payload };
      return;
    }

    const oldQty = num(map[key]?.closingQty);
    const oldTotal = num(map[key]?.closingTotal);
    const totalQty = oldQty + payload.closingQty;
    const totalValue = oldTotal + payload.closingTotal;

    map[key] = {
      closingQty: round2(totalQty),
      closingTotal: round2(totalValue),
      closingAveRate: totalQty > 0 ? round2(totalValue / totalQty) : 0,
    };
  });
};

const buildProductDetailsMap = (products = []) => {
  const byId = new Map();
  const byTitle = new Map();
  const byWarehouseProductId = new Map();
  const byWarehouseTitle = new Map();

  (Array.isArray(products) ? products : []).forEach((item) => {
    const productId = normId(item?._id || item?.id || item?.productId || "");
    const title = getProductTitleForStockKey(item);
    const warehouseId = getWarehouseIdForStockKey(item);

    if (productId) byId.set(productId, item);
    if (title && !byTitle.has(title)) byTitle.set(title, item);
    if (warehouseId && productId)
      byWarehouseProductId.set(`${warehouseId}::${productId}`, item);
    if (warehouseId && title)
      byWarehouseTitle.set(`${warehouseId}::${title}`, item);
  });

  return { byId, byTitle, byWarehouseProductId, byWarehouseTitle };
};

const findProductDetailsForStockRow = (row, productDetailsMap) => {
  const productId = getProductIdForStockKey(row);
  const warehouseId = getWarehouseIdForStockKey(row);
  const title = getProductTitleForStockKey(row);

  if (warehouseId && productId) {
    const exact = productDetailsMap?.byWarehouseProductId?.get(
      `${warehouseId}::${productId}`,
    );
    if (exact) return exact;
  }

  if (productId) {
    const byId = productDetailsMap?.byId?.get(productId);
    if (byId) return byId;
  }

  if (warehouseId && title) {
    const exactTitle = productDetailsMap?.byWarehouseTitle?.get(
      `${warehouseId}::${title}`,
    );
    if (exactTitle) return exactTitle;
  }

  if (title) return productDetailsMap?.byTitle?.get(title) || null;
  return null;
};

const mergeProductDetailsIntoStockRow = (row, productDetailsMap) => {
  const productDetails = findProductDetailsForStockRow(row, productDetailsMap);
  if (!productDetails) return row;

  return {
    ...row,
    _productDetailsFromProductList: productDetails,
    _productIdResolved: normId(
      productDetails?._id || productDetails?.id || row?.productId,
    ),
    productId: row?.productId || productDetails?._id,
    Product_Title:
      row?.Product_Title ||
      row?.productId?.Product_Title ||
      productDetails?.Product_Title ||
      productDetails?.productName ||
      "",
    HSN_Code:
      row?.HSN_Code ||
      row?.productId?.HSN_Code ||
      productDetails?.HSN_Code ||
      productDetails?.hsn ||
      "",
    openingFinancialYear: firstDefined(
      productDetails?.openingFinancialYear,
      productDetails?.OpeningFinancialYear,
      productDetails?.financialYearOpeningYear,
      productDetails?.openingFY,
      productDetails?.OpeningFY,
      productDetails?.openingYear,
      row?.openingFinancialYear,
      row?.OpeningFinancialYear,
    ),
    Opening_Stock: firstDefined(
      productDetails?.Opening_Stock,
      productDetails?.openingStock,
      productDetails?.openingQty,
      row?.Opening_Stock,
      row?.openingStock,
      row?.oQty,
    ),
    openingRate: firstDefined(
      productDetails?.openingRate,
      productDetails?.Opening_Rate,
      productDetails?.OpeningRate,
      productDetails?.oRate,
      productDetails?.purchaseRate,
      productDetails?.PurchaseRate,
      productDetails?.Product_Purchase_Rate,
      productDetails?.Purchase_Price,
      productDetails?.purchasePrice,
      productDetails?.PurchasePrice,
      productDetails?.costPrice,
      productDetails?.Product_Rate,
      row?.openingRate,
      row?.oRate,
    ),
  };
};

const loadPreviousSnapshotOpening = (database, financialYear, row) => {
  try {
    const previousFY = getPreviousFY(financialYear);
    const raw = localStorage.getItem(stockSnapshotKey(database, previousFY));
    const snapshot = raw ? JSON.parse(raw) : {};
    const item = findPreviousClosingForRow(row, snapshot);

    if (!item) return null;
    return {
      qty: num(item?.closingQty),
      rate: num(item?.closingAveRate),
    };
  } catch {
    return null;
  }
};

const getOpeningForFinancialYear = (
  row,
  financialYear,
  database,
  previousClosingMap = {},
) => {
  const openingFY = getOpeningFinancialYear(row);
  const selectedStart = parseFYStart(financialYear);
  const openingStart = openingFY ? parseFYStart(openingFY) : null;
  const savedOpening = getProductSavedOpening(row);

  if (openingFY && openingStart > selectedStart) return { qty: 0, rate: 0 };

  if (openingFY && openingStart === selectedStart) {
    return { qty: num(savedOpening?.qty), rate: num(savedOpening?.rate) };
  }

  if (openingFY && openingStart < selectedStart) {
    const previousFromApi = findPreviousClosingForRow(row, previousClosingMap);
    if (previousFromApi) {
      return {
        qty: num(previousFromApi?.closingQty),
        rate: num(previousFromApi?.closingAveRate),
      };
    }

    const previousSnapshot = loadPreviousSnapshotOpening(
      database,
      financialYear,
      row,
    );
    return previousSnapshot || { qty: 0, rate: 0 };
  }

  const backendOpening = extractBackendFYOpening(row, financialYear);
  if (backendOpening) return backendOpening;

  if (savedOpening?.qty || savedOpening?.rate) return savedOpening;

  return {
    qty: num(row?.oQty ?? row?.openingQty ?? row?.Opening_Stock),
    rate: num(row?.openingRate ?? row?.oRate),
  };
};

const normalizeClosingRowsFromResponse = (response) => {
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.closingStock)) return response.closingStock;
  if (Array.isArray(response?.Stock)) return response.Stock;
  if (Array.isArray(response?.stock)) return response.stock;
  if (!Array.isArray(response?.Warehouse)) return [];

  const rows = [];
  response.Warehouse.forEach((warehouse) => {
    if (!Array.isArray(warehouse?.productItems)) {
      rows.push(warehouse);
      return;
    }

    warehouse.productItems.forEach((item, index) => {
      const productObject =
        item?.productId && typeof item.productId === "object"
          ? item.productId
          : {};

      rows.push({
        ...item,
        _id:
          item?._id || `${warehouse?._id || warehouse?.warehouseId}-${index}`,
        warehouseId:
          warehouse?.warehouseId || warehouse?._id || warehouse?.warehouseId1,
        warehouseName: warehouse?.warehouseName || "",
        Product_Title:
          item?.Product_Title ||
          productObject?.Product_Title ||
          item?.productName ||
          "",
        HSN_Code:
          item?.HSN_Code || productObject?.HSN_Code || productObject?.hsn || "",
        productId: item?.productId,
        date:
          warehouse?.date ||
          item?.date ||
          warehouse?.createdAt ||
          item?.createdAt,
        oQty: firstDefined(item?.oQty, item?.openingQty, item?.Opening_Stock),
        openingRate: firstDefined(
          item?.openingRate,
          item?.oRate,
          item?.price,
          item?.cRate,
        ),
      });
    });
  });

  return rows;
};

const getInvoiceItemProductId = (item = {}) =>
  normId(
    item?.productId?._id ||
      item?.productId?.id ||
      item?.productId ||
      item?.ProductId ||
      item?.product_id ||
      item?.product?._id ||
      "",
  );

const getInvoiceItemTitle = (item = {}) =>
  String(
    item?.productId?.Product_Title ||
      item?.productData?.Product_Title ||
      item?.product?.Product_Title ||
      item?.Product_Title ||
      item?.productName ||
      item?.ProductName ||
      "",
  )
    .trim()
    .toLowerCase();

const buildSalesStockMap = (invoices = [], statusFilter) => {
  const bucket = {};

  const add = (key, qty, price) => {
    if (!key || qty <= 0) return;
    if (!bucket[key]) bucket[key] = { qty: 0, amount: 0 };
    bucket[key].qty += qty;
    bucket[key].amount += qty * price;
  };

  (Array.isArray(invoices) ? invoices : []).forEach((invoice) => {
    const status = String(invoice?.status || "")
      .trim()
      .toLowerCase();
    if (statusFilter && !statusFilter(status)) return;

    (Array.isArray(invoice?.orderItems) ? invoice.orderItems : []).forEach(
      (item) => {
        const qty = num(
          item?.qty ??
            item?.quantity ??
            item?.Qty ??
            item?.productQty ??
            item?.saleQty,
        );
        const price = num(
          item?.price ??
            item?.rate ??
            item?.salesRate ??
            item?.saleRate ??
            item?.productId?.price ??
            item?.productId?.salesRate ??
            item?.productId?.saleRate,
        );
        const productId = getInvoiceItemProductId(item);
        const title = getInvoiceItemTitle(item);

        if (productId) add(`id:${productId}`, qty, price);
        if (title) add(`title:${title}`, qty, price);
      },
    );
  });

  return Object.fromEntries(
    Object.entries(bucket).map(([key, value]) => [
      key,
      {
        qty: round2(value.qty),
        rate: value.qty > 0 ? round2(value.amount / value.qty) : 0,
        total: round2(value.amount),
      },
    ]),
  );
};

const buildInwardStockMap = (purchaseInvoices = []) => {
  const bucket = {};

  const add = (key, qty, rate) => {
    if (!key || qty <= 0) return;
    if (!bucket[key]) {
      bucket[key] = { qty: 0, rateSum: 0, rateCount: 0 };
    }
    bucket[key].qty += qty;
    if (rate > 0) {
      bucket[key].rateSum += rate;
      bucket[key].rateCount += 1;
    }
  };

  (Array.isArray(purchaseInvoices) ? purchaseInvoices : []).forEach(
    (invoice) => {
      const status = String(invoice?.status || "")
        .trim()
        .toLowerCase();
      if (!status.includes("completed")) return;

      (Array.isArray(invoice?.orderItems) ? invoice.orderItems : []).forEach(
        (item) => {
          const qty = num(
            item?.qty ??
              item?.quantity ??
              item?.Qty ??
              item?.productQty ??
              item?.pQty,
          );
          const rate = num(
            item?.price ??
              item?.basicPrice ??
              item?.rate ??
              item?.purchaseRate ??
              item?.PurchaseRate ??
              item?.productData?.purchaseRate ??
              item?.productData?.PurchaseRate ??
              item?.productData?.price ??
              item?.productId?.purchaseRate ??
              item?.productId?.PurchaseRate ??
              item?.productId?.price,
          );
          const productId = getInvoiceItemProductId(item);
          const title = getInvoiceItemTitle(item);

          if (productId) add(`id:${productId}`, qty, rate);
          if (title) add(`title:${title}`, qty, rate);
        },
      );
    },
  );

  return Object.fromEntries(
    Object.entries(bucket).map(([key, value]) => {
      const averageRate =
        value.rateCount > 0 ? value.rateSum / value.rateCount : 0;
      return [
        key,
        {
          qty: round2(value.qty),
          rate: round2(averageRate),
          total: round2(value.qty * averageRate),
        },
      ];
    }),
  );
};

const findStockMovementForRow = (row, stockMap = {}) => {
  const productId = getProductIdForStockKey(row);
  if (productId && stockMap[`id:${productId}`]) {
    return stockMap[`id:${productId}`];
  }

  const title = getProductTitleForStockKey(row);
  if (title && stockMap[`title:${title}`]) {
    return stockMap[`title:${title}`];
  }

  return { qty: 0, rate: 0, total: 0 };
};

const pendingStockStatuses = [
  "pending",
  "billing",
  "dispatch",
  "delivery boy assigned",
  "out for delivery",
];

const isPendingStockStatus = (status) => {
  const normalized = String(status || "")
    .trim()
    .toLowerCase();
  if (
    !normalized ||
    normalized.includes("cancel") ||
    normalized.includes("completed")
  ) {
    return false;
  }
  return pendingStockStatuses.some((code) => normalized.includes(code));
};

const buildResolvedClosing = (row, opening, outward) => {
  const openingQty = num(opening?.qty);
  const openingRate = num(opening?.rate);
  const openingTotal = openingQty * openingRate;
  const inwardQty = num(row?.pQty);
  const inwardRate = num(row?.avgPurchaseRate);
  const inwardTotal = inwardQty * inwardRate;
  const pendingQty = num(row?.pendingStock);
  const outwardQty = num(outward?.qty);

  const closingQty = round2(openingQty + inwardQty - pendingQty - outwardQty);
  const availableQty = openingQty + inwardQty;
  const availableValue = openingTotal + inwardTotal;
  const closingAveRate =
    availableQty > 0 ? round2(availableValue / availableQty) : 0;

  return {
    closingQty,
    closingAveRate,
    closingTotal: round2(closingQty * closingAveRate),
  };
};

const resolveWarehouseForStockRow = (row, warehouseMap) => {
  const warehouseId = normId(
    row?.warehouseId ??
      row?.warehouse?._id ??
      row?.warehouse ??
      row?.warehouseId1 ??
      row?.productId?.warehouse?._id ??
      row?.productId?.warehouse ??
      row?.product?.warehouse?._id ??
      row?.product?.warehouse ??
      "",
  );

  return {
    ...row,
    _warehouseIdResolved: warehouseId,
    _warehouseNameResolved:
      row?.warehouseName || warehouseMap.get(warehouseId) || "",
  };
};

const SALES_PAGE_SUMMARY_URL = "/order/sales-page-summary/";
const SALES_PAGE_BOOTSTRAP_URL = "/order/sales-page-bootstrap/";
const salesPageSummaryCache =
  window.__dmsSalesPageSummaryCache ||
  (window.__dmsSalesPageSummaryCache = new Map());
const salesPageBootstrapCache =
  window.__dmsSalesPageBootstrapCache ||
  (window.__dmsSalesPageBootstrapCache = new Map());
const SALES_CUSTOMER_OPTIONS_URL = "/order/sales-customer-options/";
const SALES_CUSTOMER_BALANCE_URL = "/order/sales-customer-balance/";
const salesCustomerOptionsCache =
  window.__dmsSalesCustomerOptionsCache ||
  (window.__dmsSalesCustomerOptionsCache = new Map());
const salesCustomerBalanceCache =
  window.__dmsSalesCustomerBalanceCache ||
  (window.__dmsSalesCustomerBalanceCache = new Map());

const clearSalesFastCaches = () => {
  salesPageSummaryCache.clear();
  salesPageBootstrapCache.clear();
  salesCustomerOptionsCache.clear();
  salesCustomerBalanceCache.clear();
};

const getSalesPageSummary = async (selectedDatabase) => {
  if (!selectedDatabase) return {};
  const now = Date.now();
  const cached = salesPageSummaryCache.get(selectedDatabase);
  if (cached && now - cached.time < 120000) return cached.promise;

  const promise = _Get(SALES_PAGE_SUMMARY_URL, encodeURIComponent(selectedDatabase)).catch(
    (error) => {
      salesPageSummaryCache.delete(selectedDatabase);
      throw error;
    },
  );
  salesPageSummaryCache.set(selectedDatabase, { time: now, promise });
  return promise;
};


const getSalesPageBootstrap = async (database, financialYear, rootUserId) => {
  if (!database || !financialYear || !rootUserId) return {};
  const key = `${database}:${financialYear}:${rootUserId}`;
  const now = Date.now();
  const cached = salesPageBootstrapCache.get(key);
  if (cached && now - cached.time < 120000) return cached.promise;

  const url = `${SALES_PAGE_BOOTSTRAP_URL}${encodeURIComponent(
    rootUserId,
  )}/${encodeURIComponent(database)}?financialYear=${encodeURIComponent(
    financialYear,
  )}&includeCustomers=0`;

  const selectedDatabase = buildSelectedDatabase(database, financialYear);
  const promise = _Get(url, "")
    .then((response) => response || {})
    .catch((error) => {
      salesPageBootstrapCache.delete(key);
      salesPageSummaryCache.delete(selectedDatabase);
      throw error;
    });

  // Seed the summary cache immediately with the bootstrap's in-flight promise.
  // This prevents party/order-number effects from starting a second history
  // aggregation while the same bootstrap request is still running.
  salesPageSummaryCache.set(selectedDatabase, {
    time: now,
    promise: promise.then((response) => response?.summary || {}),
  });
  salesPageBootstrapCache.set(key, { time: now, promise });
  return promise;
};



const formatSalesCustomerOption = (item = {}) => ({
  ...item,
  displayLabel: `${item?.CompanyName || item?.fullName || "Customer"} (Grade: ${
    item?.category?.grade ? String(item.category.grade).toUpperCase() : "NA"
  })`,
});

const getSalesCustomerOptions = async (
  database,
  rootUserId,
  searchText = "",
) => {
  if (!database || !rootUserId) return [];
  const q = String(searchText || "").trim();
  const key = `${database}:${rootUserId}:${q.toLowerCase()}`;
  const now = Date.now();
  const cached = salesCustomerOptionsCache.get(key);
  if (cached && now - cached.time < (q ? 15000 : 60000)) {
    return cached.promise;
  }

  const url = `${SALES_CUSTOMER_OPTIONS_URL}${encodeURIComponent(
    rootUserId,
  )}/${encodeURIComponent(database)}?limit=${q ? 100 : 120}&q=${encodeURIComponent(q)}`;

  const promise = _Get(url, "")
    .then((response) =>
      (Array.isArray(response?.customers) ? response.customers : []).map(
        formatSalesCustomerOption,
      ),
    )
    .catch((error) => {
      salesCustomerOptionsCache.delete(key);
      throw error;
    });

  salesCustomerOptionsCache.set(key, { time: now, promise });
  return promise;
};

const getSalesCustomerBalance = async (partyId, selectedDatabase) => {
  if (!partyId || !selectedDatabase) return {};
  const key = `${selectedDatabase}:${partyId}`;
  const now = Date.now();
  const cached = salesCustomerBalanceCache.get(key);
  if (cached && now - cached.time < 15000) return cached.promise;

  const url = `${SALES_CUSTOMER_BALANCE_URL}${encodeURIComponent(
    partyId,
  )}/${encodeURIComponent(selectedDatabase)}`;
  const promise = _Get(url, "").catch((error) => {
    salesCustomerBalanceCache.delete(key);
    throw error;
  });
  salesCustomerBalanceCache.set(key, { time: now, promise });
  return promise;
};



const buildClosingStockQtyMap = (rows = []) => {
  const map = {};

  const add = (key, qty, warehouse, shouldAggregate) => {
    if (!key) return;
    if (!map[key] || !shouldAggregate) {
      map[key] = { qty: num(qty), warehouse: warehouse || "" };
      return;
    }

    map[key] = {
      qty: round2(num(map[key]?.qty) + num(qty)),
      warehouse: map[key]?.warehouse || warehouse || "",
    };
  };

  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const productId = getProductIdForStockKey(row);
    const productTitle = getProductTitleForStockKey(row);
    const warehouseId = getWarehouseIdForStockKey(row);
    const closingQty = num(row?.closingQty ?? row?.currentStock);

    if (warehouseId && productId)
      add(`${warehouseId}::${productId}`, closingQty, warehouseId, true);
    if (warehouseId && productTitle)
      add(
        `${warehouseId}::title:${productTitle}`,
        closingQty,
        warehouseId,
        true,
      );
    if (productId) add(`id:${productId}`, closingQty, warehouseId, true);
    if (productTitle)
      add(`title:${productTitle}`, closingQty, warehouseId, true);
  });

  return map;
};

const getClosingStockLookup = (selectedItem, closingStockMap = {}) => {
  const productId = normId(selectedItem?._id || selectedItem?.productId?._id);
  const productTitle = getProductTitleForStockKey(selectedItem);
  const productWarehouseId = normId(
    selectedItem?.warehouse?._id ||
      selectedItem?.warehouse ||
      selectedItem?.warehouseId?._id ||
      selectedItem?.warehouseId,
  );

  const keys = [];
  if (productWarehouseId && productId)
    keys.push(`${productWarehouseId}::${productId}`);
  if (productWarehouseId && productTitle)
    keys.push(`${productWarehouseId}::title:${productTitle}`);
  if (productId) keys.push(`id:${productId}`);
  if (productTitle) keys.push(`title:${productTitle}`);

  for (const key of keys) {
    if (!Object.prototype.hasOwnProperty.call(closingStockMap, key)) continue;
    const item = closingStockMap[key];
    return {
      qty: Math.max(0, num(item?.qty ?? item)),
      warehouse: productWarehouseId || item?.warehouse || "",
    };
  }

  return { qty: 0, warehouse: productWarehouseId || "" };
};

const createEmptyOrderProductRow = () => ({
  productId: "",
  productData: "",
  warehouse: "",
  disCountPercentage: 0,
  availableQty: 0,
  qtyError: "",
  wholeQty: 0,
  qty: 1,
  mrp: 0,
  gradeDiscountPct: 0,
  price: 0,
  basicPrice: 0,
  salePrice: 0,
  Size: 0,
  unitType: "",
  unitPriceAfterDiscount: "",
  totalprice: "",
  taxableAmount: 0,
  gstPercentage: 0,
  sgstRate: 0,
  cgstRate: 0,
  igstRate: 0,
  grandTotal: 0,
  HSN_Code: "",
  secondaryUnit: "",
  GrossQty: 0,
  primaryUnit: "",
  profitColorCode: "",
  ProfitPercentage: 0,
});

const CUSTOMER_NEW_URL = "/app/customers/create/0";
const getCustomerEditURL = (customerId) =>
  `/app/customers/create/${customerId}`;
const PRODUCT_NEW_URL = "/app/products/create";
const getProductEditURL = (productId) => `/app/products/edit/${productId}`;

const saleFromMRPByGrade = (mrp, gradePct) =>
  floor2(Number(mrp || 0) / (1 + Number(gradePct || 0) / 100));
const basicFromSaleGST = (sale, gstPct) =>
  ceil2(Number(sale || 0) / (1 + Number(gstPct || 0) / 100));
const saleFromBasic = (basic, gstPct) =>
  round2(Number(basic || 0) * (1 + Number(gstPct || 0) / 100));
const basicFromSaleEditable = (sale, gstPct) => basicFromSaleGST(sale, gstPct);

/* ------------------------------------------------------------------ */
/* ---------------------- Adjustments Calculator --------------------- */
/* ------------------------------------------------------------------ */
const computeAdjustments = (base = 0, discountsRows = [], chargesRows = []) => {
  let running = Number(base) || 0;
  const discApplied = [];
  const chgApplied = [];

  (discountsRows || []).forEach((r) => {
    const row = { ...r };
    const typ = (row?.discounttype || "").toLowerCase();
    if (typ === "percentage") {
      const val = (running * (Number(row?.percentage) || 0)) / 100;
      row.__applied = ceil2(val);
    } else {
      row.__applied = round2(Number(row?.amount) || 0);
    }
    running -= row.__applied;
    if (running < 0) running = 0;
    discApplied.push(row);
  });

  (chargesRows || []).forEach((r) => {
    const row = { ...r };
    const typ = (row?.discounttype || "").toLowerCase();
    if (typ === "percentage") {
      const val = (running * (Number(row?.percentage) || 0)) / 100;
      row.__applied = round2(val);
    } else {
      row.__applied = round2(Number(row?.amount) || 0);
    }
    running += row.__applied;
    chgApplied.push(row);
  });

  const discountTotal = round2(
    discApplied.reduce((a, x) => a + (x.__applied || 0), 0),
  );
  const chargesTotal = round2(
    chgApplied.reduce((a, x) => a + (x.__applied || 0), 0),
  );

  return {
    discountTotal,
    chargesTotal,
    discountRowsApplied: discApplied,
    chargeRowsApplied: chgApplied,
    netAfterAdj: round2(running),
  };
};

/* ------------------------------------------------------------------ */
/* --------------------------- Component ----------------------------- */
/* ------------------------------------------------------------------ */

let geotagging = "";

const getFinancialYearLabel = (dateValue = new Date()) => {
  let year;
  let month;

  if (typeof dateValue === "string" && dateValue.includes("-")) {
    const [yyyy, mm] = dateValue.split("-");
    year = Number(yyyy);
    month = Number(mm);
  } else {
    const d = new Date(dateValue);
    year = d.getFullYear();
    month = d.getMonth() + 1;
  }

  if (!year || !month) {
    const d = new Date();
    year = d.getFullYear();
    month = d.getMonth() + 1;
  }

  const startYear = month >= 4 ? year : year - 1;
  const endYearShort = String(startYear + 1).slice(-2);

  return `${startYear}-${endYearShort}`;
};

const CreateOrder = () => {
  const roundHalfUp = (num) => {
    const frac = num - Math.floor(num);
    return frac > 0.49 ? Math.ceil(num) : Math.floor(num);
  };

  const [localRoundOff, setLocalRoundOff] = useState(0);
  const [OrderNumber, setOrderNumber] = useState("");
  const [ARN, setARN] = useState("");
  const [CustomerLimit, setCustomerLimit] = useState(0);
  const [openingLimit, setOpeningLimit] = useState(0);
  const [CustomerTerm, setCustomerTerm] = useState("");
  const [PartyLogin, setPartyLogin] = useState(false);
  const [Loading, setLoading] = useState(false);
  const [partyLoading, setPartyLoading] = useState(false);
  const [borderColor, setBorderColor] = useState("black");
  const [remarkMessage, setRemarkMessage] = useState("");
  const [remarkModel, setRemarkModel] = useState(false);

  const [ProductList, setProductList] = useState([]);
  const [GSTData, setGSTData] = useState({});
  const [PartyList, setPartyList] = useState([]);
  const [PartyId, setPartyId] = useState("");
  const [Party, setParty] = useState({});
  const [UnitList, setUnitList] = useState([]);
  const [chargesMaster, setChargesMaster] = useState([]);

  const [emergencyInvoice, setEmergencyInvoice] = useState(false);
  const [emergencyStatus, setEmergencyStatus] = useState("");
  const [lastEntryId, setLastEntryId] = useState("");
  const [UserInfo, setUserInfo] = useState({});

  const [selectedChargesRows, setSelectedChargesRows] = useState([]);
  const [selectedDiscountRows, setSelectedDiscountRows] = useState([]);

  const [finalPrice, setFinalPrice] = useState(0);
  const [taxRate, setTaxRate] = useState(0);
  const [finalPriceAfterTax, setFinalPriceAfterTax] = useState(0);
  const [grossBeforeRound, setGrossBeforeRound] = useState(0);
  const [consistencyOK, setConsistencyOK] = useState(true);
  const [recalcOnce, setRecalcOnce] = useState(false);

  const [ledgerBalance, setLedgerBalance] = useState("");
  const [pendingData, setPendingData] = useState(0);
  const [orderDate, setOrderDate] = useState("");
  const [showFYPrefixNotice, setShowFYPrefixNotice] = useState(false);
  const [currentFYLabel, setCurrentFYLabel] = useState("");
  const [closingStockQtyMap, setClosingStockQtyMap] = useState({});
  const [closingStockLoading, setClosingStockLoading] = useState(true);
  const [closingStockError, setClosingStockError] = useState("");
  const [draftReady, setDraftReady] = useState(false);

  const [product, setProduct] = useState([
    {
      productId: "",
      productData: "",
      warehouse: "",
      disCountPercentage: 0,
      availableQty: 0,
      qtyError: "",
      wholeQty: 0,
      qty: 1,
      mrp: 0,
      gradeDiscountPct: 0,
      price: 0,
      basicPrice: 0,
      salePrice: 0,
      Size: 0,
      unitType: "",
      unitPriceAfterDiscount: "",
      totalprice: "",
      taxableAmount: 0,
      gstPercentage: 0,
      sgstRate: 0,
      cgstRate: 0,
      igstRate: 0,
      grandTotal: 0,
      HSN_Code: "",
      secondaryUnit: "",
      GrossQty: 0,
      primaryUnit: "",
      profitColorCode: "",
      ProfitPercentage: 0,
    },
  ]);

  const multiselectRefs = useRef(product.map(() => React.createRef()));
  const Context = useContext(UserContext);
  const navigate = useNavigate();
  const partyMultiselectRef = useRef(null);

  const getOrderDraftKey = () => {
    const userData = JSON.parse(localStorage.getItem("userData") || "{}");
    const financialYear =
      localStorage.getItem("financialYearSelected") ||
      getFinancialYearLabel(new Date());
    return `CREATE_SALES_ORDER_DRAFT:${userData?.database || "default"}:${financialYear}`;
  };

  useEffect(() => {
    try {
      const rawDraft = localStorage.getItem(getOrderDraftKey());
      if (rawDraft) {
        const draft = JSON.parse(rawDraft);

        setOrderNumber(draft?.OrderNumber || "");
        setARN(draft?.ARN || "");
        setPartyId(draft?.PartyId || "");
        setParty(draft?.Party || {});
        setProduct(
          Array.isArray(draft?.product) && draft.product.length
            ? draft.product.map((row) => ({
                ...createEmptyOrderProductRow(),
                ...row,
              }))
            : [createEmptyOrderProductRow()],
        );
        setSelectedChargesRows(
          Array.isArray(draft?.selectedChargesRows)
            ? draft.selectedChargesRows
            : [],
        );
        setSelectedDiscountRows(
          Array.isArray(draft?.selectedDiscountRows)
            ? draft.selectedDiscountRows
            : [],
        );
        setOrderDate(draft?.orderDate || "");
        setRemarkMessage(draft?.remarkMessage || "");
        setCustomerLimit(num(draft?.CustomerLimit));
        setOpeningLimit(num(draft?.openingLimit));
        setCustomerTerm(draft?.CustomerTerm || "");
        setBorderColor(draft?.borderColor || "black");
        setLedgerBalance(draft?.ledgerBalance || "");
        setPendingData(num(draft?.pendingData));
        setGSTData(draft?.GSTData || {});
        setFinalPrice(num(draft?.finalPrice));
        setTaxRate(num(draft?.taxRate));
        setFinalPriceAfterTax(num(draft?.finalPriceAfterTax));
        setGrossBeforeRound(num(draft?.grossBeforeRound));
        setLocalRoundOff(num(draft?.localRoundOff));
      }
    } catch (error) {
      console.warn("Unable to restore order draft", error);
    } finally {
      setDraftReady(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!draftReady) return undefined;

    const timer = window.setTimeout(() => {
      const hasEnteredData =
        !!PartyId ||
        !!OrderNumber ||
        !!ARN ||
        !!remarkMessage ||
        product.some((row) => !!row?.productId) ||
        selectedChargesRows.length > 0 ||
        selectedDiscountRows.length > 0;

      const draftKey = getOrderDraftKey();
      if (!hasEnteredData) {
        localStorage.removeItem(draftKey);
        return;
      }

      try {
        localStorage.setItem(
          draftKey,
          JSON.stringify({
            version: 1,
            savedAt: new Date().toISOString(),
            OrderNumber,
            ARN,
            PartyId,
            Party,
            product,
            selectedChargesRows,
            selectedDiscountRows,
            orderDate,
            remarkMessage,
            CustomerLimit,
            openingLimit,
            CustomerTerm,
            borderColor,
            ledgerBalance,
            pendingData,
            GSTData,
            finalPrice,
            taxRate,
            finalPriceAfterTax,
            grossBeforeRound,
            localRoundOff,
          }),
        );
      } catch (error) {
        console.warn("Unable to save order draft", error);
      }
    }, 250);

    return () => window.clearTimeout(timer);
  }, [
    draftReady,
    OrderNumber,
    ARN,
    PartyId,
    Party,
    product,
    selectedChargesRows,
    selectedDiscountRows,
    orderDate,
    remarkMessage,
    CustomerLimit,
    openingLimit,
    CustomerTerm,
    borderColor,
    ledgerBalance,
    pendingData,
    GSTData,
    finalPrice,
    taxRate,
    finalPriceAfterTax,
    grossBeforeRound,
    localRoundOff,
  ]);

  useEffect(() => {
    const handleSilent404Rejection = (event) => {
      if (isAxios404(event?.reason)) {
        event.preventDefault();
        event.stopImmediatePropagation?.();
      }
    };

    window.addEventListener("unhandledrejection", handleSilent404Rejection);
    return () => {
      window.removeEventListener(
        "unhandledrejection",
        handleSilent404Rejection,
      );
    };
  }, []);

  useEffect(() => {
    const id = "invoice-compact-css";
    if (document.getElementById(id)) return;
    const s = document.createElement("style");
    s.id = id;
    s.textContent = `
    .compact-ui{
      --box-w: clamp(68px, 6.8vw, 110px);
      --gap: 6px;
      --fs: 13px;
      --pad: 6px;
      --label-fs: 12px;
    }
    .compact-ui label{ font-size: var(--label-fs); margin-bottom: 4px; font-weight: 600; line-height: 1.1; }
    .compact-ui .card-body{ padding: 12px; }
    .compact-ui .form-control,
    .compact-ui .form-select{
      height: calc(28px + var(--pad));
      min-height: calc(28px + var(--pad));
      padding: var(--pad) 10px;
      font-size: var(--fs);
      border-radius: 10px;
      line-height: 1.15;
    }
.compact-ui .top-line{
  --topcol-w: clamp(160px, 14vw, 210px);
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: var(--topcol-w);
  gap: 8px;
  align-items: end;
}
.compact-ui .top-line > [class*="col"]{ width: var(--topcol-w); min-width: var(--topcol-w); padding-right: 0; }
.compact-ui .top-line .multiselect-container .searchWrapper{ height: calc(28px + var(--pad)); }
    .compact-ui .btn{ --bs-btn-padding-y: 4px; --bs-btn-padding-x: 10px; font-size: 12px; line-height: 1.1; border-radius: 10px; }
    .compact-ui .btn-sm{ padding: 3px 8px; font-size: 11.5px; border-radius: 9px; }
    .compact-ui .table, .compact-ui table{ font-size: 12px; }
    .compact-ui .table > :not(caption) > * > *{ padding: 4px 6px; }
    .compact-ui .inline-row{ display: grid; grid-auto-flow: column; grid-auto-columns: var(--box-w); gap: var(--gap); align-items: end; overflow-x: hidden; }
    .compact-ui .auto-cell{ width: var(--box-w); min-width: var(--box-w); }
    .compact-ui .auto-cell.w-wide{ grid-column: span 2; }
    .compact-ui .chip{ display: inline-flex; align-items: center; gap: 2px; padding: 2px 4px; border-radius: 9999px; font-size: 12px; background: #f3f4f6; border: 1px solid #e5e7eb; }
    .compact-ui .chip input.form-control, .compact-ui .chip select.form-select{ height: calc(26px + var(--pad)); min-width: 60px; padding: var(--pad) 8px; font-size: 12px; }
    .compact-ui .totals-ul{ width: max-content; min-width: 240px; font-size: 13px; }
    .compact-ui .totals-ul li{ padding: 4px 0; gap: 10px; }
    .compact-ui .adjustments-inline{ display: grid; grid-auto-flow: column; grid-auto-columns: max-content; gap: 8px; align-items: end; overflow-x: hidden; }
    @media (min-width: 1600px){ .compact-ui{ --box-w: 120px; } }
    @media (max-width: 1280px){ .compact-ui{ --box-w: 82px; } }
  `;
    document.head.appendChild(s);
  }, []);

  useEffect(() => {
    const id = "multiselect-chip-fix";
    if (document.getElementById(id)) return;
    const s = document.createElement("style");
    s.id = id;
    s.textContent = `
      .multiselect-container .searchWrapper{ overflow: visible !important; }
      .multiselect-container .chip{ display: inline-flex !important; align-items: center; gap: 6px; }
      .multiselect-container .chip > span{ white-space: nowrap !important; }
    `;
    document.head.appendChild(s);
  }, []);

  // Charges are loaded by the Sales bootstrap request together with products,
  // customers, units and stock. This avoids another startup API call.


  const sumLineBasics = (rows) =>
    (rows || []).reduce(
      (s, r) =>
        s + Number(r?.qty || 0) * Number(r?.basicPrice || r?.price || 0),
      0,
    );

  const recalcTotals = (
    _chargesRows = selectedChargesRows,
    _discountRows = selectedDiscountRows,
    _productRows = product,
    _gstData = GSTData,
  ) => {
    // IMPORTANT:
    // React state updates are async. So after qty/price/product change, this
    // function must calculate from the rows passed to it, not always from the
    // old `product` state. This fixes wrong Taxable Value / Grand Total.
    const rows = Array.isArray(_productRows) ? _productRows : [];
    const gstData = _gstData || {};

    const basicTotal = round2(sumLineBasics(rows));

    const {
      chargesTotal,
      discountRowsApplied,
      chargeRowsApplied,
      netAfterAdj,
    } = computeAdjustments(basicTotal, _discountRows, _chargesRows);

    setSelectedDiscountRows(discountRowsApplied);
    setSelectedChargesRows(chargeRowsApplied);

    const maxItemGST =
      rows.reduce(
        (m, p) => Math.max(m, Number(parseGstPct(p?.gstPercentage || 0))),
        0,
      ) || 0;

    const isIGST = !!gstData?.Tax?.IgstTaxType;
    const chargesGST = (Number(chargesTotal || 0) * maxItemGST) / 100;
    const half = chargesGST / 2;

    let addIGST = 0,
      addCGST = 0,
      addSGST = 0;
    if (isIGST) addIGST = chargesGST;
    else {
      addCGST = half;
      addSGST = half;
    }

    const gstRows = Array.isArray(gstData?.gstDetails)
      ? gstData.gstDetails
      : [];
    const gstFromRows = gstRows.reduce(
      (acc, row) => {
        acc.cgst += Number(row?.cgstRate || 0);
        acc.sgst += Number(row?.sgstRate || 0);
        acc.igst += Number(row?.igstRate || row?.igstTax?.[0]?.amount || 0);
        return acc;
      },
      { cgst: 0, sgst: 0, igst: 0 },
    );

    const itemIGST = Number(gstData?.Tax?.IgstTotal || 0) || gstFromRows.igst;
    const itemCGST = Number(gstData?.Tax?.CgstTotal || 0) || gstFromRows.cgst;
    const itemSGST = Number(gstData?.Tax?.SgstTotal || 0) || gstFromRows.sgst;

    const itemsGST = isIGST ? itemIGST : itemCGST + itemSGST;

    const gross =
      Number(netAfterAdj || 0) +
      Number(itemsGST || 0) +
      Number(addIGST + addCGST + addSGST);

    setFinalPrice(Number(netAfterAdj).toFixed(2));
    setTaxRate(Number(addIGST + addCGST + addSGST));
    setGrossBeforeRound(round2(gross));

    const rounded = roundHalfUp(gross);
    const roundOff = Number(Math.abs(rounded - gross).toFixed(2));

    setLocalRoundOff(roundOff);
    setFinalPriceAfterTax(rounded);
  };

  const hsnSummary = useMemo(() => {
    const details = Array.isArray(GSTData?.gstDetails)
      ? GSTData.gstDetails
      : [];
    const isIGST = !!GSTData?.Tax?.IgstTaxType;

    const byHSN = new Map();
    details.forEach((r) => {
      const hsn = String(r?.hsn || "NA");
      const ratePct = parseGstPct(r?.gstPercentage || 0);
      const g = byHSN.get(hsn) || {
        hsn,
        ratePct,
        taxable: 0,
        cgst: 0,
        sgst: 0,
        igst: 0,
      };
      g.ratePct = ratePct;
      g.taxable += Number(r?.taxable || 0);
      g.cgst += Number(r?.cgstRate || 0);
      g.sgst += Number(r?.sgstRate || 0);
      g.igst += Number(r?.igstRate || 0);
      byHSN.set(hsn, g);
    });

    let maxRate = 0;
    for (const v of byHSN.values()) maxRate = Math.max(maxRate, v.ratePct);

    const chargesAmt = (selectedChargesRows || []).reduce(
      (s, x) => s + (Number(x?.__applied) || 0),
      0,
    );
    const chargesTax = (chargesAmt * maxRate) / 100;

    const rows = Array.from(byHSN.values())
      .map((r) => ({
        kind: "ITEM",
        hsn: r.hsn,
        ratePct: r.ratePct,
        taxable: round2(r.taxable),
        cgst: round2(r.cgst),
        sgst: round2(r.sgst),
        igst: round2(r.igst),
        total: round2(r.taxable + r.cgst + r.sgst + r.igst),
      }))
      .sort((a, b) => b.ratePct - a.ratePct);

    if (chargesAmt > 0) {
      rows.push({
        kind: "CHARGE",
        hsn: `CHARGES @ ${maxRate}%`,
        ratePct: maxRate,
        taxable: round2(chargesAmt),
        cgst: isIGST ? 0 : round2(chargesTax / 2),
        sgst: isIGST ? 0 : round2(chargesTax / 2),
        igst: isIGST ? round2(chargesTax) : 0,
        total: round2(chargesAmt + chargesTax),
      });
    }

    const totals = rows.reduce(
      (acc, r) => ({
        taxable: acc.taxable + r.taxable,
        cgst: acc.cgst + (r.cgst || 0),
        sgst: acc.sgst + (r.sgst || 0),
        igst: acc.igst + (r.igst || 0),
        total: acc.total + r.total,
      }),
      { taxable: 0, cgst: 0, sgst: 0, igst: 0, total: 0 },
    );

    return { rows, totals, isIGST };
  }, [GSTData, selectedChargesRows]);

  const hasQtyError = useMemo(
    () =>
      product.some(
        (row) =>
          !!row?.productId &&
          (Number(row?.availableQty || 0) <= 0 ||
            Number(row?.qty || 0) > Number(row?.availableQty || 0) ||
            !!row?.qtyError),
      ),
    [product],
  );

  useEffect(() => {
    const hsnTotal = round2(hsnSummary?.totals?.total || 0);
    const invoiceGross = round2(grossBeforeRound || 0);

    // Do not fail only because of round-off / paise difference.
    // Normal bill round-off can create a difference below ₹1.
    const diff = Math.abs(hsnTotal - invoiceGross);
    const ok = diff < 1;

    setConsistencyOK(ok);

    if (!ok && !recalcOnce) {
      setRecalcOnce(true);
      const gstd = GstCalculation(
        Party,
        product,
        Context,
        selectedDiscountRows,
      );
      setGSTData(gstd);
      recalcTotals(selectedChargesRows, selectedDiscountRows, product, gstd);
    }

    if (ok && recalcOnce) setRecalcOnce(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    grossBeforeRound,
    hsnSummary?.totals?.total,
    Party,
    product,
    selectedDiscountRows,
    selectedChargesRows,
  ]);

  const unique3Digit = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem("userData"));
      const financialYearSelected = localStorage.getItem("financialYearSelected");
      const selectedDatabase = `${userData?.database}-${financialYearSelected}`;
      const summary = await getSalesPageSummary(selectedDatabase);
      const previous = Array.isArray(summary?.activeOrderNumbers)
        ? summary.activeOrderNumbers.map(Number).filter(Number.isFinite).sort((a, b) => a - b)
        : [];

      for (let i = 1; i <= previous.length; i++) {
        if (!previous.includes(i)) return i.toString().padStart(3, "0");
      }
      const next = previous.length > 0 ? Math.max(...previous) + 1 : 1;
      return next.toString().padStart(3, "0");
    } catch {
      return "001";
    }
  };

  const partySearchTimerRef = useRef(null);
  const partySearchRequestRef = useRef(0);

  const handlePartySearch = (searchText) => {
    if (partySearchTimerRef.current) {
      window.clearTimeout(partySearchTimerRef.current);
    }
    const requestNo = ++partySearchRequestRef.current;
    partySearchTimerRef.current = window.setTimeout(async () => {
      try {
        const userData = JSON.parse(localStorage.getItem("userData") || "{}");
        const hierarchyRoot =
          userData?.rolename?.roleName === "SuperAdmin"
            ? userData?._id
            : userData?.created_by || userData?._id;
        const customers = await getSalesCustomerOptions(
          userData?.database,
          hierarchyRoot,
          searchText,
        );
        if (requestNo === partySearchRequestRef.current) {
          setPartyList(customers);
        }
      } catch {}
    }, 180);
  };

  const handleSelectionParty = async (_selectedList, selectedItem) => {
    setPartyLoading(true);
    try {
      setOpeningLimit(selectedItem?.limit);

      // Do not rebuild the full live ledger on customer selection.
      // Credit-customer balance is loaded below from one tiny aggregate API.
      setLedgerBalance("");
      setPendingData(0);

      const uniqueNumber = await unique3Digit();
      setOrderNumber(uniqueNumber);
      setPartyId(selectedItem._id);
      setParty(selectedItem);

      const partyGrade = Number(selectedItem?.category?.discount || 0);
      const updatedProductRows = product.map((row) => {
        if (!row?.productData) return row;
        const mrp = Number(row?.productData?.Product_MRP || 0);
        const gstPct = Number(row?.productData?.GSTRate || 0);
        const sale = saleFromMRPByGrade(mrp, partyGrade);
        const basic = basicFromSaleGST(sale, gstPct);
        const qty = Number(row?.qty || 0);

        return {
          ...row,
          mrp,
          gradeDiscountPct: partyGrade,
          gstPercentage: gstPct,
          salePrice: sale,
          basicPrice: basic,
          price: basic,
          taxableAmount: round2(qty * basic),
        };
      });

      setProduct(updatedProductRows);

      const gstdetails = GstCalculation(
        selectedItem,
        updatedProductRows,
        Context,
        selectedDiscountRows,
      );
      setGSTData(gstdetails);
      recalcTotals(
        selectedChargesRows,
        selectedDiscountRows,
        updatedProductRows,
        gstdetails,
      );

      const isCash = (selectedItem?.paymentTerm || "")
        .toLowerCase()
        .includes("cash");

      if (isCash) {
        setCustomerLimit(0);
        setCustomerTerm("Cash");
        setBorderColor("green");
      } else {
        try {
          const userData = JSON.parse(localStorage.getItem("userData") || "{}");
          const financialYearSelected =
            localStorage.getItem("financialYearSelected") || "";
          const selectedDatabase = `${userData?.database}-${financialYearSelected}`;

          const balance = await getSalesCustomerBalance(
            selectedItem?._id,
            selectedDatabase,
          );
          const adjustedPending = Number(balance?.adjustedPending || 0);
          const finalLimit = Number(
            balance?.availableLimit ?? selectedItem?.limit ?? 0,
          );

          setLedgerBalance(
            balance?.ledgerBalance ||
              `${Math.abs(Number(balance?.netBalance || 0))} (${
                balance?.balanceType || "Debit"
              })`,
          );
          setPendingData(adjustedPending);
          setCustomerLimit(finalLimit);

          const grandTotal = Number(gstdetails?.Tax?.GrandTotal || 0);
          setBorderColor(grandTotal < finalLimit ? "green" : "red");
          setCustomerTerm("");
        } catch {
          setCustomerLimit(selectedItem?.limit || 0);
          setBorderColor("green");
        }
      }

      const newDetails = GstCalculation(
        selectedItem,
        updatedProductRows,
        Context,
        selectedDiscountRows,
      );
      setGSTData(newDetails);
      recalcTotals(
        selectedChargesRows,
        selectedDiscountRows,
        updatedProductRows,
        newDetails,
      );
    } finally {
      setPartyLoading(false);
    }
  };

  const onRemoveParty = () => {
    setPartyId("");
    setParty({});
    setGSTData({});
    setCustomerLimit(0);
    setBorderColor("black");
    multiselectRefs.current.forEach((ref) =>
      ref?.current?.resetSelectedValues?.(),
    );
    setProduct([
      {
        productId: "",
        productData: "",
        warehouse: "",
        disCountPercentage: 0,
        availableQty: 0,
        qtyError: "",
        wholeQty: 0,
        qty: 1,
        mrp: 0,
        gradeDiscountPct: 0,
        price: 0,
        basicPrice: 0,
        salePrice: 0,
        Size: 0,
        unitType: "",
        unitPriceAfterDiscount: "",
        totalprice: "",
        taxableAmount: 0,
        gstPercentage: 0,
        sgstRate: 0,
        cgstRate: 0,
        igstRate: 0,
        grandTotal: 0,
        HSN_Code: "",
        secondaryUnit: "",
        GrossQty: 0,
        primaryUnit: "",
        profitColorCode: "",
        ProfitPercentage: 0,
      },
    ]);
    setSelectedChargesRows([]);
    setSelectedDiscountRows([]);
    setFinalPrice(0);
    setTaxRate(0);
    setFinalPriceAfterTax(0);
    setGrossBeforeRound(0);
    setConsistencyOK(true);
  };

  const getAvailableClosingQty = (selectedItem) =>
    getClosingStockLookup(selectedItem, closingStockQtyMap).qty;

  const getAvailableWarehouseId = (selectedItem) =>
    getClosingStockLookup(selectedItem, closingStockQtyMap).warehouse;

  const handleSelection = async (_selectedList, selectedItem, index) => {
    const closingAvailableQty = getAvailableClosingQty(selectedItem);
    const availableWarehouseId = getAvailableWarehouseId(selectedItem);
    const partyGrade = Number(Party?.category?.discount || 0);
    const mrp = Number(selectedItem?.Product_MRP || 0);
    const gstPct = Number(selectedItem?.GSTRate || 0);

    const sale = saleFromMRPByGrade(mrp, partyGrade);
    const basic = basicFromSaleGST(sale, gstPct);

    setProduct((prev) => {
      const updated = [...prev];
      const row = { ...(updated[index] || {}) };

      row.productId = selectedItem?._id;
      row.warehouse = availableWarehouseId;
      row.productData = selectedItem;
      row.mrp = mrp;
      row.gradeDiscountPct = partyGrade;
      row.gstPercentage = gstPct;
      row.salePrice = sale;
      row.basicPrice = basic;
      row.price = basic;
      row.HSN_Code = selectedItem?.HSN_Code;
      row.primaryUnit = selectedItem?.primaryUnit;
      row.secondaryUnit = selectedItem?.secondaryUnit;
      row.secondarySize = selectedItem?.secondarySize;
      row.GrossQty = Number(
        (Number(row?.qty || 0) / (selectedItem?.secondarySize || 1)).toFixed(3),
      );
      row.disCountPercentage = partyGrade;
      row.availableQty = closingAvailableQty;
      row.wholeQty = closingAvailableQty;
      row.qtyError =
        closingAvailableQty <= 0 || Number(row?.qty || 0) > closingAvailableQty
          ? "Qty not available"
          : "";
      row.profitColorCode = selectedItem?.profitColorCode || "";
      row.ProfitPercentage = Number(selectedItem?.ProfitPercentage || 0);

      updated[index] = row;

      const gstdetails = GstCalculation(
        Party,
        updated,
        Context,
        selectedDiscountRows,
      );
      setGSTData(gstdetails);

      const detail = gstdetails?.gstDetails?.[index] || {};
      row.taxableAmount = round2(
        (row.qty || 0) * (row.basicPrice || row.price || 0),
      );
      row.sgstRate = detail?.sgstRate || 0;
      row.cgstRate = detail?.cgstRate || 0;
      row.igstRate = detail?.igstTax?.[0]?.amount || 0;
      row.grandTotal = detail?.grandTotal || 0;

      updated[index] = row;
      recalcTotals(
        selectedChargesRows,
        selectedDiscountRows,
        updated,
        gstdetails,
      );

      return updated;
    });
  };

  const handleProductDropdownChange = (e, index) => {
    const selectedId = e.target.value;
    if (!selectedId) {
      onRemove1([], {}, index);
      return;
    }
    const selectedItem = ProductList.find((item) => item?._id === selectedId);
    if (!selectedItem) return;

    console.log("Selected Product Information:", selectedItem);

    handleSelection([], selectedItem, index);
  };

  const onRemove1 = (_selectedList, _removedItem, index) => {
    const updated = [...product];
    updated[index] = {
      productId: "",
      productData: "",
      warehouse: "",
      disCountPercentage: 0,
      availableQty: 0,
      qtyError: "",
      wholeQty: 0,
      qty: 1,
      mrp: 0,
      gradeDiscountPct: 0,
      price: 0,
      basicPrice: 0,
      salePrice: 0,
      Size: 0,
      unitType: "",
      unitPriceAfterDiscount: "",
      totalprice: "",
      taxableAmount: 0,
      gstPercentage: 0,
      sgstRate: 0,
      cgstRate: 0,
      igstRate: 0,
      grandTotal: 0,
      HSN_Code: "",
      secondaryUnit: "",
      GrossQty: 0,
      primaryUnit: "",
      profitColorCode: "",
      ProfitPercentage: 0,
    };
    setProduct(updated);

    const gstdetails = GstCalculation(
      Party,
      updated,
      Context,
      selectedDiscountRows,
    );
    setGSTData(gstdetails);
    recalcTotals(
      selectedChargesRows,
      selectedDiscountRows,
      updated,
      gstdetails,
    );
  };

  const handleRequredQty = (e, index, availableQty, CustomerTermState) => {
    const list = [...product];
    const { name, value } = e.target;

    const asNum = value === "" ? "" : Number(value);
    if (value !== "" && isNaN(asNum)) return;

    const nextNetQty =
      name === "GrossQty"
        ? Number(((list[index]?.secondarySize || 1) * (asNum || 0)).toFixed(3))
        : asNum || 0;

    if (value !== "" && nextNetQty < 0) return;

    if (name === "GrossQty") {
      list[index].GrossQty = value === "" ? 0 : asNum;
      list[index].qty = nextNetQty;
    } else {
      list[index][name] = value === "" ? 0 : asNum;
      list[index].GrossQty = Number(
        ((list[index].qty || 0) / (list[index]?.secondarySize || 1)).toFixed(3),
      );
    }

    list[index].qtyError =
      Number(availableQty || 0) <= 0 ||
      Number(list[index]?.qty || 0) > Number(availableQty || 0)
        ? "Qty not available"
        : "";

    const gstdetails = GstCalculation(
      Party,
      list,
      Context,
      selectedDiscountRows,
    );
    setGSTData(gstdetails);

    const gstInfo = gstdetails?.gstDetails?.[index] || {};
    list[index].taxableAmount = round2(
      (list[index]?.qty || 0) *
        (list[index]?.basicPrice || list[index]?.price || 0),
    );
    list[index].sgstRate = gstInfo?.sgstRate || 0;
    list[index].cgstRate = gstInfo?.cgstRate || 0;
    list[index].igstRate = gstInfo?.igstTax?.[0]?.amount || 0;
    list[index].grandTotal = gstInfo?.grandTotal || 0;
    list[index].gstPercentage = gstInfo?.gstPercentage || 0;
    list[index].disCountPercentage = gstInfo?.discountPercentage || 0;

    setProduct(list);

    if (CustomerTermState !== "Cash") {
      if ((gstdetails?.Tax?.GrandTotal || 0) < (CustomerLimit || 0)) {
        setBorderColor("green");
      } else {
        setBorderColor(emergencyStatus === "Active" ? "green" : "red");
      }
    }

    recalcTotals(selectedChargesRows, selectedDiscountRows, list, gstdetails);
  };

  const handleChangePrice = (e, index) => {
    const { name, value } = e.target;
    const v = value === "" ? "" : Number(value);
    if (value !== "" && (isNaN(v) || v <= 0)) return;

    const list = [...product];
    if (!list[index]) return;

    const row = { ...list[index] };
    const gstPct = parseGstPct(row?.gstPercentage);

    if (name === "basicPrice") {
      row.basicPrice = v === "" ? 0 : v;
      row.price = v === "" ? 0 : v;
      row.salePrice = saleFromBasic(v || 0, gstPct);
    } else if (name === "salePrice") {
      row.salePrice = v === "" ? 0 : v;
      const basic = basicFromSaleEditable(v || 0, gstPct);
      row.basicPrice = basic;
      row.price = basic;
    } else {
      row[name] = v === "" ? 0 : v;
    }

    list[index] = row;

    const gstdetails = GstCalculation(
      Party,
      list,
      Context,
      selectedDiscountRows,
    );
    setGSTData(gstdetails);

    if (gstdetails?.gstDetails && gstdetails?.gstDetails[index]) {
      row.taxableAmount = round2(
        (row.qty || 0) * (row.basicPrice || row.price || 0),
      );
      row.sgstRate = gstdetails.gstDetails[index]?.sgstRate || 0;
      row.cgstRate = gstdetails.gstDetails[index]?.cgstRate || 0;
      row.igstRate = gstdetails.gstDetails[index]?.igstTax?.[0]?.amount || 0;
      row.grandTotal = gstdetails.gstDetails[index]?.grandTotal || 0;
    }

    list[index] = row;
    setProduct(list);

    if (CustomerTerm !== "Cash") {
      if ((gstdetails?.Tax?.GrandTotal || 0) < (CustomerLimit || 0)) {
        setBorderColor("green");
      } else {
        setBorderColor(emergencyStatus === "Active" ? "green" : "red");
      }
    }
    recalcTotals(selectedChargesRows, selectedDiscountRows, list, gstdetails);
  };

  const addMoreProduct = () => {
    setProduct([
      ...product,
      {
        productId: "",
        productData: "",
        warehouse: "",
        disCountPercentage: 0,
        availableQty: 0,
        qtyError: "",
        wholeQty: 0,
        qty: 1,
        mrp: 0,
        gradeDiscountPct: 0,
        price: 0,
        basicPrice: 0,
        salePrice: 0,
        Size: 0,
        unitType: "",
        unitPriceAfterDiscount: "",
        totalprice: "",
        taxableAmount: 0,
        gstPercentage: 0,
        sgstRate: 0,
        cgstRate: 0,
        igstRate: 0,
        grandTotal: 0,
        HSN_Code: "",
        secondaryUnit: "",
        GrossQty: 0,
        primaryUnit: "",
        profitColorCode: "",
        ProfitPercentage: 0,
      },
    ]);
  };

  const removeMoreProduct = (i) => {
    const newFormValues = [...product];
    newFormValues.splice(i, 1);
    setProduct(newFormValues);
    const gstdetails = GstCalculation(
      Party,
      newFormValues,
      Context,
      selectedDiscountRows,
    );
    setGSTData(gstdetails);
    recalcTotals(
      selectedChargesRows,
      selectedDiscountRows,
      newFormValues,
      gstdetails,
    );
  };

  const addChargeRow = () =>
    setSelectedChargesRows((prev) => [
      ...prev,
      {
        id: "",
        title: "",
        type: "Charges",
        discounttype: "",
        percentage: "",
        amount: "",
        __applied: 0,
      },
    ]);

  const addDiscountRow = () =>
    setSelectedDiscountRows((prev) => [
      ...prev,
      {
        id: "",
        title: "",
        type: "Discount",
        discounttype: "",
        percentage: "",
        amount: "",
        __applied: 0,
      },
    ]);

  const removeChargeRow = (i) => {
    const arr = [...selectedChargesRows];
    arr.splice(i, 1);
    setSelectedChargesRows(arr);
    recalcTotals(arr, selectedDiscountRows);
  };

  const removeDiscountRow = (i) => {
    const arr = [...selectedDiscountRows];
    arr.splice(i, 1);
    setSelectedDiscountRows(arr);
    const gstdetails = GstCalculation(Party, product, Context, arr);
    setGSTData(gstdetails);
    recalcTotals(selectedChargesRows, arr);
  };

  const pickMasterRow = (rowType, idx, id) => {
    const master = chargesMaster.find((x) => x._id === id);
    if (!master) return;

    const rowData = {
      id: master._id,
      title: master.title,
      type: master.type,
      discounttype: master.discounttype,
      percentage: master.percentage || "",
      amount: master.amount || "",
      __applied: 0,
    };

    if (rowType === "Charges") {
      const arr = [...selectedChargesRows];
      arr[idx] = rowData;
      setSelectedChargesRows(arr);
      recalcTotals(arr, selectedDiscountRows);
    } else {
      const arr = [...selectedDiscountRows];
      arr[idx] = rowData;
      const gstdetails = GstCalculation(Party, product, Context, arr);
      setGSTData(gstdetails);
      setSelectedDiscountRows(arr);
      recalcTotals(selectedChargesRows, arr);
    }
  };

  const changeRowField = (rowType, idx, key, value) => {
    const norm = (k, v) => {
      if (k === "percentage" || k === "amount") {
        if (v === "" || v === null) return "";
        const n = Number(v);
        return isNaN(n) ? "" : n;
      }
      return v;
    };

    if (rowType === "Charges") {
      const arr = [...selectedChargesRows];
      arr[idx] = { ...arr[idx], [key]: norm(key, value) };
      setSelectedChargesRows(arr);
      recalcTotals(arr, selectedDiscountRows);
    } else {
      const arr = [...selectedDiscountRows];
      arr[idx] = { ...arr[idx], [key]: norm(key, value) };
      setSelectedDiscountRows(arr);
      const gstdetails = GstCalculation(Party, product, Context, arr);
      setGSTData(gstdetails);
      recalcTotals(selectedChargesRows, arr);
    }
  };

  const onAddAdjustmentSelect = (e) => {
    const id = e.target.value;
    if (!id) return;
    e.target.value = "";
    const master = chargesMaster.find((x) => x._id === id);
    if (!master) return;

    const rowData = {
      id: master._id,
      title: master.title,
      type: master.type,
      discounttype: master.discounttype,
      percentage: master.percentage || "",
      amount: master.amount || "",
      __applied: 0,
    };

    if ((master.type || "").toLowerCase().includes("discount")) {
      const arr = [...selectedDiscountRows, rowData];
      setSelectedDiscountRows(arr);
      const gstdetails = GstCalculation(Party, product, Context, arr);
      setGSTData(gstdetails);
      recalcTotals(selectedChargesRows, arr);
    } else {
      const arr = [...selectedChargesRows, rowData];
      setSelectedChargesRows(arr);
      recalcTotals(arr, selectedDiscountRows);
    }
  };

  const buildDiscountDetailsPayload = (discRowsApplied, chgRowsApplied) => {
    const mapRow = (x, asType) => {
      const isPct = (x?.discounttype || "").toLowerCase() === "percentage";
      const percentage = isPct ? Number(x?.percentage || 0) : 0;

      // IMPORTANT: amount must be saved from Applied value
      const appliedAmount = round2(Number(x?.__applied || 0));

      return {
        title: x?.title || asType,
        type: asType,
        discounttype: x?.discounttype || "",
        disType: isPct ? "Percentage" : "Amount",
        percentage: percentage || 0,

        // THIS IS THE FIX
        amount: appliedAmount,

        discountedAmount: appliedAmount,
        discountedValue: isPct ? percentage : appliedAmount,
      };
    };

    const cleanedDiscounts = (discRowsApplied || [])
      .filter((x) => Number(x?.__applied || 0) > 0)
      .map((x) => mapRow(x, "Discount"));

    const cleanedCharges = (chgRowsApplied || [])
      .filter((x) => Number(x?.__applied || 0) > 0)
      .map((x) => mapRow(x, "Charges"));

    return [...cleanedDiscounts, ...cleanedCharges];
  };

  const handleProceed = async () => {
    try {
      setLoading(true);

      const invalidStockIndex = product.findIndex(
        (row) =>
          !!row?.productId &&
          (Number(row?.availableQty || 0) <= 0 ||
            Number(row?.qty || 0) > Number(row?.availableQty || 0)),
      );

      if (invalidStockIndex >= 0) {
        setProduct((prev) =>
          prev.map((row, index) =>
            index === invalidStockIndex
              ? { ...row, qtyError: "Qty not available" }
              : row,
          ),
        );
        setLoading(false);
        swal("Qty not available");
        return;
      }

      if (borderColor === "red" && emergencyStatus !== "Active") {
        setLoading(false);
        swal(
          "Limit exceeded",
          "Request an emergency order and wait for approval.",
          "warning",
        );
        return;
      }

      if (!consistencyOK) {
        setLoading(false);
        swal(
          "Recalculating totals",
          "HSN Summary and order totals did not match. Please wait a second for auto-recalc and submit again.",
          "warning",
        );
        return;
      }

      const basicTotal = sumLineBasics(product);

      const {
        chargesTotal,
        discountTotal,
        discountRowsApplied,
        chargeRowsApplied,
        netAfterAdj,
      } = computeAdjustments(
        basicTotal,
        selectedDiscountRows,
        selectedChargesRows,
      );

      const igstTaxType = !!GSTData?.Tax?.IgstTaxType;

      const highestGstPct = (() => {
        let maxPct = 0;
        (product || []).forEach((p) => {
          const g = parseGstPct(p?.gstPercentage);
          if (!isNaN(g) && g > maxPct) maxPct = g;
        });
        (GSTData?.gstDetails || []).forEach((row) => {
          const g = parseGstPct(row?.gstPercentage);
          if (!isNaN(g) && g > maxPct) maxPct = g;
        });
        return maxPct || 0;
      })();

      let chargesCgst = 0,
        chargesSgst = 0,
        chargesIgst = 0;
      if (chargesTotal > 0 && highestGstPct > 0) {
        const taxOnCharges = (chargesTotal * highestGstPct) / 100;
        if (igstTaxType) chargesIgst = taxOnCharges;
        else {
          chargesCgst = taxOnCharges / 2;
          chargesSgst = taxOnCharges / 2;
        }
      }

      const baseSgst = Number(GSTData?.Tax?.SgstTotal || 0);
      const baseCgst = Number(GSTData?.Tax?.CgstTotal || 0);
      const baseIgst = Number(GSTData?.Tax?.IgstTotal || 0);
      const roundOff = Number(GSTData?.Tax?.RoundOff || 0);

      const finalSgst = igstTaxType ? 0 : baseSgst + chargesSgst;
      const finalCgst = igstTaxType ? 0 : baseCgst + chargesCgst;
      const finalIgst = igstTaxType ? baseIgst + chargesIgst : 0;

      const itemsGrand = Array.isArray(GSTData?.gstDetails)
        ? GSTData.gstDetails.reduce(
            (acc, row) => acc + (Number(row?.grandTotal) || 0),
            0,
          )
        : 0;

      const grandTotal =
        itemsGrand +
        chargesTotal +
        chargesCgst +
        chargesSgst +
        chargesIgst +
        roundOff;

      const discountDetails = buildDiscountDetailsPayload(
        discountRowsApplied,
        chargeRowsApplied,
      );

      const orderItemsPayload = product.map((ele) => {
        const qty = Number(ele?.qty) || 0;
        const base = Number(ele?.basicPrice || ele?.price || 0);

        return {
          productId: ele?.productId,
          warehouse:
            ele?.productData?.warehouse?._id ||
            ele?.productData?.warehouse ||
            ele?.warehouse?._id ||
            ele?.warehouse ||
            "",
          discountPercentage: Number(ele?.disCountPercentage) || 0,
          qty,
          price: base,
          salePrice: Number(ele?.salePrice) || 0,
          primaryUnit: ele?.primaryUnit,
          secondaryUnit: ele?.secondaryUnit,
          secondarySize: ele?.secondarySize,
          totalPrice: Number(qty * base),
          sgstRate: Number(ele?.sgstRate) || 0,
          cgstRate: Number(ele?.cgstRate) || 0,
          gstPercentage: parseGstPct(ele?.gstPercentage),
          igstRate: Number(ele?.igstRate) || 0,
          grandTotal: Number(ele?.grandTotal) || 0,
          taxableAmount: round2(qty * base),
          totalPriceWithDiscount: Number(ele?.grandTotal) || 0,
        };
      });

      const arnNumber = Number(grandTotal.toFixed(2));
      const ARNStatus = arnNumber > 49999;

      const fullname = Party?.CompanyName || Party?.fullName || "";
      const userData = JSON.parse(localStorage.getItem("userData")) || {};
      const financialYearSelected = localStorage.getItem(
        "financialYearSelected",
      );
      const selectedDatabase = `${userData?.database}-${financialYearSelected}`;
      console.log(selectedDatabase);
      const companyDetail =
        JSON.parse(localStorage.getItem("Companydetail")) || {};
      const upiId = companyDetail?.bankDetails?.[0]?.upiId || "";
      const accountNumber =
        companyDetail?.bankDetails?.[0]?.accountNumber || "";
      const bankIFSC = companyDetail?.bankDetails?.[0]?.bankIFSC || "";
      const bankDetails = companyDetail?.bankDetails?.[0]?._id || "";
      const merchantName = companyDetail?.name || "";

      const payload = {
        userId: Party?.created_by?._id,
        partyId: Party?._id || PartyId,
        invoice: OrderNumber, // ORDER NUMBER - this creates an order, not an invoice
        basicPrice: Number(netAfterAdj.toFixed(2)),
        database: selectedDatabase,
        financialYear: getFinancialYearLabel(orderDate),
        ARN: ARNStatus ? ARN : "",
        ARNStatus,
        discountPercentage: Party?.category
          ? Party?.category?.discount || 0
          : 0,
        SuperAdmin:
          Context?.CompanyDetails?.created_by ||
          userData?.created_by ||
          userData?._id,
        fullName: fullname,
        address: Party?.address,
        igstTaxType,
        grandTotal: Number(grandTotal.toFixed(2)),
        roundOff: Number(roundOff.toFixed(2)),
        ChargesCgst: Number(chargesCgst.toFixed(2)),
        ChargesSgst: Number(chargesSgst.toFixed(2)),
        ChargesIgst: Number(chargesIgst.toFixed(2)),
        amount: Number(netAfterAdj.toFixed(2)),
        sgstTotal: Number(finalSgst.toFixed(2)),
        cgstTotal: Number(finalCgst.toFixed(2)),
        igstTotal: Number(finalIgst.toFixed(2)),
        gstDetails: GSTData?.gstDetails || [],
        hsnData:
          GSTData?.groupedHSN && Array.isArray(GSTData?.groupedHSN)
            ? GSTData?.groupedHSN
            : hsnSummary?.rows || [],
        MobileNo: Party?.contactNumber,
        pincode: Party?.pincode,
        state: Party?.State,
        date: orderDate,
        city: Party?.City,
        orderItems: orderItemsPayload,
        DateofDelivery: "",
        geotagging: geotagging,
        discountDetails,
        upiId,
        accountNumber,
        bankDetails,
        bankIFSC,
        merchantName,
      };

      if (!payload.partyId) {
        swal("Select a customer first.");
        setLoading(false);
        return;
      }
      if (!payload.invoice) {
        swal("Enter an order number.");
        setLoading(false);
        return;
      }

      const res = await SaveOrder(payload);

      if (res?.status === false) {
        swal(res?.message || "Something went wrong while creating order");
        return;
      }

      localStorage.removeItem(getOrderDraftKey());

      if (emergencyInvoice || emergencyStatus === "Active") {
        const Payload = {
          count: 0,
          status: "Completed",
          database: selectedDatabase,
          remark: remarkMessage,
          partyId: Party?._id || PartyId,
        };
        await _PutUpdate(updateEmergencyInvoice, lastEntryId, Payload);
        setEmergencyStatus("Completed");
        setEmergencyInvoice(false);
      }

      swal("Order Created Successfully");
      clearSalesFastCaches();
      navigate("/app/sales-flow");
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Something went wrong while creating order";
      swal(msg);
    } finally {
      setLoading(false);
    }
  };

  const clearOrderData = () => {
    const userData = JSON.parse(localStorage.getItem("userData") || "{}");
    const isCustomer = userData?.rolename?.roleName === "Customer";

    localStorage.removeItem(getOrderDraftKey());
    partyMultiselectRef.current?.resetSelectedValues?.();

    setOrderNumber("");
    setARN("");
    setPartyId(isCustomer ? userData?._id || "" : "");
    setParty(isCustomer ? userData : {});
    setProduct([createEmptyOrderProductRow()]);
    setSelectedChargesRows([]);
    setSelectedDiscountRows([]);
    setGSTData({});
    setOrderDate(new Date().toISOString().slice(0, 10));
    setRemarkMessage("");
    setCustomerLimit(0);
    setOpeningLimit(0);
    setCustomerTerm(isCustomer ? userData?.paymentTerm || "" : "");
    setBorderColor("black");
    setLedgerBalance("");
    setPendingData(0);
    setFinalPrice(0);
    setTaxRate(0);
    setFinalPriceAfterTax(0);
    setGrossBeforeRound(0);
    setLocalRoundOff(0);
    setConsistencyOK(true);
    setEmergencyInvoice(false);
    setEmergencyStatus("");
    setLastEntryId("");

    swal("Order data cleared");
  };

  const submitHandler = async (e) => {
    e.preventDefault();
    handleProceed();
  };

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          geotagging = `${position.coords.latitude},${position.coords.longitude}`;
        },
        () => {},
        { timeout: 5000, enableHighAccuracy: false },
      );
    }

    const userdata = JSON.parse(localStorage.getItem("userData") || "{}");
    const findParty = userdata?.rolename?.roleName === "Customer";
    if (findParty) {
      setPartyLogin(true);
      setPartyId(userdata?._id);
      setParty(userdata);
      _Get("order/check-party-limit/", userdata?._id)
        .then((res) => setCustomerLimit(Number(res?.CustomerLimit)))
        .catch(() => {});
    }

    let cancelled = false;
    (async () => {
      setClosingStockLoading(true);
      setClosingStockError("");
      try {
        const selectedFY =
          localStorage.getItem("financialYearSelected") ||
          getFinancialYearLabel(new Date());
        const hierarchyRoot =
          userdata?.rolename?.roleName === "SuperAdmin"
            ? userdata?._id
            : userdata?.created_by || userdata?._id;

        getSalesCustomerOptions(userdata?.database, hierarchyRoot)
          .then((customers) => {
            if (!cancelled) setPartyList(customers);
          })
          .catch(() => {});

        const fast = await getSalesPageBootstrap(
          userdata?.database,
          selectedFY,
          hierarchyRoot,
        );
        if (cancelled) return;

        const products = Array.isArray(fast?.products) ? fast.products : [];
        const units = Array.isArray(fast?.units) ? fast.units : [];
        const charges = Array.isArray(fast?.otherCharges)
          ? fast.otherCharges
          : [];

        setProductList(products);
        setUnitList(units);
        setChargesMaster(charges);

        const resolvedRows = products.map((item) => {
          const stock = fast?.stockMap?.[String(item?._id)] || {};
          return {
            ...item,
            productId: item?._id,
            warehouseId: stock?.warehouse || item?.warehouse || "",
            warehouse: stock?.warehouse || item?.warehouse || "",
            closingQty: num(stock?.qty),
            currentStock: num(stock?.qty),
            closingAveRate: num(stock?.rate),
          };
        });
        const stockMap = buildClosingStockQtyMap(resolvedRows);
        setClosingStockQtyMap(stockMap);

        setProduct((previousRows) =>
          previousRows.map((row) => {
            if (!row?.productId) return row;
            const latestProduct =
              products.find(
                (item) => String(item?._id) === String(row?.productId),
              ) || row?.productData;
            const stock = getClosingStockLookup(latestProduct, stockMap);
            return {
              ...row,
              productData: latestProduct || row?.productData,
              warehouse: stock.warehouse || row?.warehouse || "",
              availableQty: stock.qty,
              wholeQty: stock.qty,
              qtyError:
                stock.qty <= 0 || Number(row?.qty || 0) > stock.qty
                  ? "Qty not available"
                  : "",
            };
          }),
        );
      } catch (error) {
        silentApiError(error);
        if (!cancelled) {
          setClosingStockQtyMap({});
          setClosingStockError("Sales data could not be loaded.");
        }
      } finally {
        if (!cancelled) setClosingStockLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const today = new Date();
    const formattedDate = today.toISOString().slice(0, 10);
    setOrderDate((previousDate) => previousDate || formattedDate);
    const userInfo = JSON.parse(localStorage.getItem("userData"));
    setUserInfo(userInfo);
  }, []);

  useEffect(() => {
    if (!PartyId || !PartyList.length) return;
    const latestParty = PartyList.find(
      (item) => String(item?._id) === String(PartyId),
    );
    if (latestParty) setParty(latestParty);
  }, [PartyId, PartyList]);

  useEffect(() => {
    if (!ProductList.length) return;
    setProduct((previousRows) =>
      previousRows.map((row) => {
        if (!row?.productId) return row;
        const latestProduct = ProductList.find(
          (item) => String(item?._id) === String(row?.productId),
        );
        return latestProduct ? { ...row, productData: latestProduct } : row;
      }),
    );
  }, [ProductList]);

  const handleFYPrefixDone = (e) => {
    if (e.target.checked && currentFYLabel) {
      const storageKey = `prefix_suffix_done_${currentFYLabel}`;
      localStorage.setItem(storageKey, "true");
      setShowFYPrefixNotice(false);
    }
  };

  useEffect(() => {
    const today = new Date();
    const month = today.getMonth(); // January = 0, April = 3
    const year = today.getFullYear();

    // Financial year first month = April
    if (month === 3) {
      const fyLabel = `${year}-${year + 1}`;
      const storageKey = `prefix_suffix_done_${fyLabel}`;

      setCurrentFYLabel(fyLabel);

      const alreadyDone = localStorage.getItem(storageKey) === "true";

      if (!alreadyDone) {
        setShowFYPrefixNotice(true);
      }
    } else {
      setShowFYPrefixNotice(false);
    }
  }, []);

  const toggleRemarkModal = () => setRemarkModel(!remarkModel);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem("userData") || "{}");
    const financialYearSelected = localStorage.getItem("financialYearSelected");
    const selectedDatabase = `${userData?.database}-${financialYearSelected}`;
    const selectedPartyId = Party?._id || PartyId;

    if (!userData?.database || !selectedPartyId) {
      setEmergencyStatus("");
      return;
    }

    const url = `${viewEmergencyInvoice}${encodeURIComponent(
      selectedDatabase,
    )}?partyId=${encodeURIComponent(selectedPartyId)}`;

    _Get(url, "")
      .then((res) => {
        const latest = Array.isArray(res?.emergencyInvoice)
          ? res.emergencyInvoice[0]
          : null;
        setLastEntryId(latest?._id || "");
        setEmergencyStatus(latest?.status || "");
        if (latest?.status === "Active") setBorderColor("green");
      })
      .catch((err) => {
        silentApiError(err);
        setEmergencyStatus("");
      });
  }, [Party?._id, PartyId, emergencyInvoice]);

  const styles = {
    headerRow: { margin: "0.5rem" },
    title: { margin: 0, fontSize: 22, fontWeight: 600 },
    right: { display: "flex", justifyContent: "flex-end" },
    fieldBox: { marginBottom: 8 },
    label: { fontWeight: 600, fontSize: 12 },
    smallNote: { fontSize: 12, color: "red" },
    totalWrap: { display: "flex", justifyContent: "flex-end", marginTop: 8 },
  };

  return (
    <div className="invoice-ui" style={{ position: "relative" }}>
      {showFYPrefixNotice && (
        <div
          style={{
            background: "#fff7ed",
            border: "1px solid #fb923c",
            color: "#9a3412",
            padding: "10px 14px",
            borderRadius: "10px",
            marginBottom: "12px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "12px",
            fontSize: "14px",
            fontWeight: 600,
          }}
        >
          <span>
            Attention: Kindly change your Prefix and Suffix as per the Financial
            Year {currentFYLabel}.
          </span>

          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              cursor: "pointer",
              margin: 0,
              whiteSpace: "nowrap",
            }}
          >
            <input type="checkbox" onChange={handleFYPrefixDone} />
            Done
          </label>
        </div>
      )}
      {partyLoading && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(255,255,255,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2000,
            backdropFilter: "blur(1px)",
          }}
        >
          <div
            style={{
              background: "white",
              border: "1px solid #e5e7eb",
              borderRadius: 12,
              padding: "16px 20px",
              display: "flex",
              alignItems: "center",
              gap: 12,
              boxShadow:
                "0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -4px rgba(0,0,0,0.1)",
            }}
          >
            <Spinner />
            <div style={{ fontWeight: 600, fontSize: 14 }}>
              Loading customer data…
            </div>
          </div>
        </div>
      )}

      <Card>
        <Row style={styles.headerRow}>
          <Col>
            <h1 style={styles.title}>Create Sales Order</h1>
          </Col>
          <Col style={styles.right}>
            <div className="d-flex gap-2">
              <Button color="warning" type="button" onClick={clearOrderData}>
                Clear
              </Button>
              <Button color="danger" type="button" onClick={() => navigate(-1)}>
                Back
              </Button>
            </div>
          </Col>
        </Row>

        <CardBody>
          <Form onSubmit={submitHandler}>
            {!PartyLogin && (
              <div className="top-strip">
                <div className="box party-box">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <Label>
                      Choose Party <span style={{ color: "red" }}>*</span>
                    </Label>
                    <div className="d-flex gap-1">
                      <Button
                        size="sm"
                        color="primary"
                        type="button"
                        className="btn-xxs"
                        onClick={() => navigate(CUSTOMER_NEW_URL)}
                      >
                        New
                      </Button>
                      {!!PartyId && (
                        <Button
                          size="sm"
                          color="secondary"
                          type="button"
                          className="btn-xxs"
                          onClick={() => navigate(getCustomerEditURL(PartyId))}
                        >
                          Edit
                        </Button>
                      )}
                    </div>
                  </div>

                  <Multiselect
                    ref={partyMultiselectRef}
                    required
                    selectionLimit={1}
                    isObject
                    options={PartyList}
                    selectedValues={Party?._id ? [Party] : []}
                    onSelect={handleSelectionParty}
                    onRemove={onRemoveParty}
                    onSearch={handlePartySearch}
                    displayValue="displayLabel"
                  />

                  {(borderColor === "red" || emergencyStatus) && (
                    <small
                      style={{
                        color: "blue",
                        cursor: "pointer",
                        display: "inline-block",
                        marginTop: 5,
                      }}
                      onClick={() => setRemarkModel(true)}
                      title="Click to request or view status"
                    >
                      <p style={{ margin: 0 }}>
                        Emergency order:&nbsp;
                        <span
                          style={{
                            color:
                              emergencyStatus === "Active"
                                ? "green"
                                : emergencyStatus === "Pending"
                                  ? "orange"
                                  : emergencyStatus === "Completed"
                                    ? "green"
                                    : "red",
                            fontWeight: 600,
                          }}
                        >
                          {emergencyStatus || "Click to request"}
                        </span>
                      </p>
                    </small>
                  )}
                </div>

                <div className="box orderdate-box">
                  <Label>
                    Order Date <span style={{ color: "red" }}>*</span>
                  </Label>
                  <Input
                    required
                    type="date"
                    name="orderDate"
                    value={orderDate}
                    onChange={(e) => setOrderDate(e.target.value)}
                  />
                </div>

                <div className="box invoiceno-box">
                  <Label>
                    Order Number <span style={{ color: "red" }}>*</span>
                  </Label>
                  <Input
                    required
                    type="text"
                    placeholder="Enter Order Number"
                    name="OrderNumber"
                    value={OrderNumber}
                    onChange={(e) => setOrderNumber(e.target.value)}
                  />
                </div>

                {Number(finalPriceAfterTax || GSTData?.Tax?.GrandTotal || 0) >
                  49999 && (
                  <div className="box arn-box">
                    <Label>
                      ARN Number <span style={{ color: "red" }}>*</span>
                    </Label>
                    <Input
                      type="text"
                      placeholder="Enter ARN Number"
                      name="ARN"
                      value={ARN}
                      onChange={(e) => setARN(e.target.value)}
                    />
                  </div>
                )}

                {CustomerLimit > 0 ? (
                  <>
                    <div className="box sanction-box">
                      <Label>Sanction Amount</Label>
                      <Input
                        readOnly
                        type="text"
                        name="CustomerLimit"
                        value={Number(openingLimit || 0).toFixed(2)}
                      />
                      <div className="note-red">Ledger B: {ledgerBalance}</div>
                    </div>

                    <div className="box pending-box">
                      <Label>Pending Amount</Label>
                      <Input
                        readOnly
                        type="text"
                        value={Number(pendingData || 0).toFixed(2)}
                      />
                      <div className="note-red">Pending Sales</div>
                    </div>

                    <div className="box balance-box">
                      <Label>Balance Amount</Label>
                      <Input
                        readOnly
                        type="text"
                        value={Number(CustomerLimit || 0).toFixed(2)}
                      />
                    </div>

                    <div className="box netbalance-box">
                      <Label style={{ color: borderColor }}>Net Balance</Label>
                      <Input
                        readOnly
                        type="text"
                        style={{ border: `3px solid ${borderColor}` }}
                        value={
                          !!GSTData?.Tax?.GrandTotal
                            ? (
                                CustomerLimit - (GSTData?.Tax?.GrandTotal || 0)
                              ).toFixed(2)
                            : CustomerLimit
                        }
                      />
                    </div>
                  </>
                ) : (
                  <div className="box balance-box">
                    <Label>Payment Mode *</Label>
                    <Input readOnly type="text" value={CustomerTerm} />
                  </div>
                )}
              </div>
            )}

            {closingStockError && (
              <div style={{ color: "red", fontSize: 12, marginTop: 8 }}>
                {closingStockError}
              </div>
            )}

            {product?.length > 0 &&
              product?.map((p, index) => (
                <div key={index} className="prod-row mt-2">
                  <div className="box prod-select-box">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <Label>
                        Select Product <span style={{ color: "red" }}>*</span>
                      </Label>
                      <div className="d-flex gap-1">
                        <Button
                          size="sm"
                          color="primary"
                          type="button"
                          className="btn-xxs"
                          onClick={() => navigate(PRODUCT_NEW_URL)}
                        >
                          New
                        </Button>
                        {!!p?.productId && (
                          <Button
                            size="sm"
                            color="secondary"
                            type="button"
                            className="btn-xxs"
                            onClick={() =>
                              navigate(getProductEditURL(p.productId))
                            }
                          >
                            Edit
                          </Button>
                        )}
                      </div>
                    </div>

                    <Input
                      type="select"
                      required
                      disabled={closingStockLoading}
                      value={p?.productId || ""}
                      onChange={(e) => handleProductDropdownChange(e, index)}
                      style={{
                        fontWeight: 600,
                      }}
                    >
                      <option value="">
                        {closingStockLoading
                          ? "Loading stock..."
                          : "Select Product"}
                      </option>
                      {ProductList.map((item) => (
                        <option
                          key={item?._id}
                          value={item?._id}
                          style={{
                            color: item?.profitColorCode || "#212529",
                            fontWeight: 600,
                          }}
                        >
                          {item?.Product_Title}{" "}
                          {/* {item?.ProfitPercentage !== undefined &&
                          item?.ProfitPercentage !== null
                            ? `(Profit: ${Number(item?.ProfitPercentage || 0).toFixed(2)}%)`
                            : ""} */}
                        </option>
                      ))}
                    </Input>
                  </div>

                  <div className="box hsn-box">
                    <Label>HSN Code</Label>
                    <Input readOnly value={p?.HSN_Code || ""} />
                  </div>

                  <div className="box grossunit-box">
                    <Label>Gross Unit</Label>
                    <Input readOnly value={p?.secondaryUnit || ""} />
                  </div>

                  <div className="box grossqty-box">
                    <Label>Gross Qty</Label>
                    <Input
                      required
                      type="number"
                      step="any"
                      name="GrossQty"
                      value={p?.GrossQty || 0}
                      onChange={(e) =>
                        handleRequredQty(
                          e,
                          index,
                          p?.availableQty,
                          CustomerTerm,
                        )
                      }
                    />
                  </div>

                  <div className="box availqty-box">
                    <Label>Avail Qty *</Label>
                    <Input
                      readOnly
                      value={p?.availableQty || 0}
                      style={{
                        border: p?.qtyError ? "2px solid red" : undefined,
                        color: p?.qtyError ? "red" : undefined,
                        fontWeight: 700,
                      }}
                    />
                    {p?.qtyError && (
                      <div className="note-red" style={{ fontWeight: 700 }}>
                        Qty not available
                      </div>
                    )}
                  </div>

                  <div className="box netqty-box">
                    <Label>Net Qty *</Label>
                    <Input
                      required
                      type="number"
                      name="qty"
                      min={1}
                      style={{
                        border: p?.qtyError ? "2px solid red" : undefined,
                      }}
                      value={p?.qty ?? 1}
                      onChange={(e) =>
                        handleRequredQty(
                          e,
                          index,
                          p?.availableQty,
                          CustomerTerm,
                        )
                      }
                    />
                  </div>

                  <div className="box netunit-box">
                    <Label>Net Unit</Label>
                    <Input disabled value={p?.primaryUnit || ""} />
                  </div>

                  <div className="box mrp-box">
                    <Label>MRP</Label>
                    <Input readOnly value={(p?.mrp || 0).toFixed(3)} />
                  </div>

                  <div className="box gradedisc-box">
                    <Label>Grade Disc (%)</Label>
                    <Input readOnly value={p?.gradeDiscountPct || 0} />
                  </div>

                  <div className="box saleprice-box">
                    <Label>Sale Price (incl GST)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      name="salePrice"
                      value={p?.salePrice || 0}
                      onChange={(e) => handleChangePrice(e, index)}
                    />
                  </div>

                  <div className="box basicprice-box">
                    <Label>Basic Price (ex GST)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      name="basicPrice"
                      value={p?.basicPrice || 0}
                      onChange={(e) => handleChangePrice(e, index)}
                    />
                  </div>

                  <div className="box gst-box">
                    <Label>GST %</Label>
                    <Input disabled value={p?.gstPercentage || 0} />
                  </div>

                  <div className="box basictotal-box">
                    <Label>Basic Total</Label>
                    <Input
                      disabled
                      value={
                        p?.taxableAmount
                          ? Number(p?.taxableAmount).toFixed(2)
                          : 0
                      }
                    />
                  </div>

                  <div className="box remove-box">
                    {product?.length > 1 ? (
                      <Button
                        type="button"
                        color="danger"
                        className="btn-xxs"
                        onClick={() => removeMoreProduct(index)}
                        style={{ marginTop: 24 }}
                        aria-label="Remove row"
                      >
                        X
                      </Button>
                    ) : null}
                  </div>
                </div>
              ))}

            <div className="d-flex justify-content-end mt-2">
              <Button color="primary" size="sm" onClick={addMoreProduct}>
                Add Product
              </Button>
            </div>

            <div className="justify-content-total jt-container mt-3">
              <div className="left-tables jt-left">
                <div className="mini-title jt-title">Bill Adjustments</div>

                <div className="adjust-toolbar jt-toolbar">
                  <div className="box adjust-addselect jt-addselect">
                    <Label className="jt-addselect-label">
                      Add Discount / Charge
                    </Label>
                    <Input
                      type="select"
                      className="jt-addselect-input"
                      onChange={onAddAdjustmentSelect}
                    >
                      <option value="">-- Select from master --</option>
                      {chargesMaster.map((x) => (
                        <option key={x._id} value={x._id}>
                          {x.title} ({x.type})
                        </option>
                      ))}
                    </Input>
                  </div>

                  <Button
                    size="sm"
                    color="success"
                    className="btn-xxs jt-btn jt-btn-discount"
                    onClick={addDiscountRow}
                  >
                    + Discount
                  </Button>
                  <Button
                    size="sm"
                    color="primary"
                    className="btn-xxs jt-btn jt-btn-charge"
                    onClick={addChargeRow}
                  >
                    + Charge
                  </Button>
                </div>

                <div className="adjust-table jt-table">
                  <div className="adj-row adj-head jt-row jt-head">
                    <div className="jt-h-idx">#</div>
                    <div className="jt-h-type">Type</div>
                    <div className="jt-h-title">Title / Master</div>
                    <div className="jt-h-method">Method</div>
                    <div className="jt-h-pct">%</div>
                    <div className="jt-h-amt">Amount</div>
                    <div className="jt-h-applied">Applied</div>
                    <div className="jt-h-action">Action</div>
                  </div>

                  {selectedDiscountRows.map((r, i) => {
                    const isPct =
                      (r.discounttype || "").toLowerCase() === "percentage";
                    return (
                      <div
                        className="adj-row jt-row jt-discount-row"
                        key={`disc-${i}`}
                      >
                        <div className="c-idx jt-idx">{i + 1}</div>
                        <div className="c-type jt-type">Discount</div>

                        <div className="c-title jt-title-cell">
                          <Input
                            type="select"
                            className="jt-input jt-select jt-title-select"
                            value={r.id || ""}
                            onChange={(e) =>
                              pickMasterRow("Discount", i, e.target.value)
                            }
                          >
                            <option value="">-- Select from master --</option>
                            {chargesMaster
                              .filter((m) =>
                                (m.type || "")
                                  .toLowerCase()
                                  .includes("discount"),
                              )
                              .map((m) => (
                                <option key={m._id} value={m._id}>
                                  {m.title}
                                </option>
                              ))}
                          </Input>
                        </div>

                        <div className="c-method jt-method-cell">
                          <Input
                            type="select"
                            className="jt-input jt-select jt-method-select"
                            value={r.discounttype || ""}
                            onChange={(e) =>
                              changeRowField(
                                "Discount",
                                i,
                                "discounttype",
                                e.target.value,
                              )
                            }
                          >
                            <option value="">Choose</option>
                            <option value="Percentage">Percentage</option>
                            <option value="Amount">Amount</option>
                          </Input>
                        </div>

                        <div className="c-pct jt-pct-cell">
                          <Input
                            type="number"
                            inputMode="decimal"
                            placeholder="%"
                            className="jt-input jt-number jt-pct-input"
                            disabled={!isPct}
                            value={r.percentage === "" ? "" : r.percentage}
                            onChange={(e) =>
                              changeRowField(
                                "Discount",
                                i,
                                "percentage",
                                e.target.value,
                              )
                            }
                          />
                        </div>

                        <div className="c-amt jt-amt-cell">
                          <Input
                            type="number"
                            inputMode="decimal"
                            placeholder="₹"
                            className="jt-input jt-number jt-amt-input"
                            disabled={isPct}
                            value={r.amount === "" ? "" : r.amount}
                            onChange={(e) =>
                              changeRowField(
                                "Discount",
                                i,
                                "amount",
                                e.target.value,
                              )
                            }
                          />
                        </div>

                        <div className="c-applied jt-applied-cell">
                          <Input
                            readOnly
                            className="jt-input jt-readonly jt-applied-input"
                            value={r.__applied ?? 0}
                          />
                        </div>

                        <div className="c-action jt-action-cell">
                          <Button
                            color="danger"
                            size="sm"
                            className="btn-xxs jt-btn jt-delete-btn"
                            onClick={() => removeDiscountRow(i)}
                            aria-label="Delete"
                          >
                            🗑
                          </Button>
                        </div>
                      </div>
                    );
                  })}

                  {selectedChargesRows.map((r, i) => {
                    const isPct =
                      (r.discounttype || "").toLowerCase() === "percentage";
                    return (
                      <div
                        className="adj-row jt-row jt-charge-row"
                        key={`charge-${i}`}
                      >
                        <div className="c-idx jt-idx">{i + 1}</div>
                        <div className="c-type jt-type">Charge</div>

                        <div className="c-title jt-title-cell">
                          <Input
                            type="select"
                            className="jt-input jt-select jt-title-select"
                            value={r.id || ""}
                            onChange={(e) =>
                              pickMasterRow("Charges", i, e.target.value)
                            }
                          >
                            <option value="">-- Select from master --</option>
                            {chargesMaster
                              .filter((m) =>
                                (m.type || "").toLowerCase().includes("charge"),
                              )
                              .map((m) => (
                                <option key={m._id} value={m._id}>
                                  {m.title}
                                </option>
                              ))}
                          </Input>
                        </div>

                        <div className="c-method jt-method-cell">
                          <Input
                            type="select"
                            className="jt-input jt-select jt-method-select"
                            value={r.discounttype || ""}
                            onChange={(e) =>
                              changeRowField(
                                "Charges",
                                i,
                                "discounttype",
                                e.target.value,
                              )
                            }
                          >
                            <option value="">Choose</option>
                            <option value="Percentage">Percentage</option>
                            <option value="Amount">Amount</option>
                          </Input>
                        </div>

                        <div className="c-pct jt-pct-cell">
                          <Input
                            type="number"
                            inputMode="decimal"
                            placeholder="%"
                            className="jt-input jt-number jt-pct-input"
                            disabled={!isPct}
                            value={r.percentage === "" ? "" : r.percentage}
                            onChange={(e) =>
                              changeRowField(
                                "Charges",
                                i,
                                "percentage",
                                e.target.value,
                              )
                            }
                          />
                        </div>

                        <div className="c-amt jt-amt-cell">
                          <Input
                            type="number"
                            inputMode="decimal"
                            placeholder="₹"
                            className="jt-input jt-number jt-amt-input"
                            disabled={isPct}
                            value={r.amount === "" ? "" : r.amount}
                            onChange={(e) =>
                              changeRowField(
                                "Charges",
                                i,
                                "amount",
                                e.target.value,
                              )
                            }
                          />
                        </div>

                        <div className="c-applied jt-applied-cell">
                          <Input
                            readOnly
                            className="jt-input jt-readonly jt-applied-input"
                            value={r.__applied ?? 0}
                          />
                        </div>

                        <div className="c-action jt-action-cell">
                          <Button
                            color="danger"
                            size="sm"
                            className="btn-xxs jt-btn jt-delete-btn"
                            onClick={() => removeChargeRow(i)}
                            aria-label="Delete"
                          >
                            🗑
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {hsnSummary.rows.length > 0 && (
                <div className="mt-2 jt-hsn-block">
                  <div className="mini-title jt-hsn-title">HSN Summary</div>
                  <Table bordered size="sm" className="jt-hsn-table">
                    <thead className="jt-hsn-thead">
                      <tr className="jt-hsn-headrow">
                        <th className="jt-hsn-th jt-hsn-col-hsn">HSN/SAC</th>
                        <th className="jt-hsn-th jt-hsn-col-taxable text-end">
                          TAXABLE VALUE
                        </th>
                        <th className="jt-hsn-th jt-hsn-col-rate text-center">
                          TAX RATE
                        </th>
                        {!hsnSummary.isIGST && (
                          <th className="jt-hsn-th jt-hsn-col-cgst text-end">
                            CGST
                          </th>
                        )}
                        {!hsnSummary.isIGST && (
                          <th className="jt-hsn-th jt-hsn-col-sgst text-end">
                            SGST
                          </th>
                        )}
                        {hsnSummary.isIGST && (
                          <th className="jt-hsn-th jt-hsn-col-igst text-end">
                            IGST
                          </th>
                        )}
                        <th className="jt-hsn-th jt-hsn-col-total text-end">
                          TOTAL
                        </th>
                      </tr>
                    </thead>
                    <tbody className="jt-hsn-tbody">
                      {hsnSummary.rows.map((r, i) => (
                        <tr
                          className="jt-hsn-row"
                          key={`hsn-${r.kind}-${r.hsn}-${i}`}
                        >
                          <td className="jt-hsn-td jt-hsn-col-hsn">{r.hsn}</td>
                          <td className="jt-hsn-td jt-hsn-col-taxable text-end">
                            {r.taxable.toFixed(2)}
                          </td>
                          <td className="jt-hsn-td jt-hsn-col-rate text-center">
                            {`${r.ratePct}%`}
                          </td>
                          {!hsnSummary.isIGST && (
                            <td className="jt-hsn-td jt-hsn-col-cgst text-end">
                              {(r.cgst || 0).toFixed(2)}
                            </td>
                          )}
                          {!hsnSummary.isIGST && (
                            <td className="jt-hsn-td jt-hsn-col-sgst text-end">
                              {(r.sgst || 0).toFixed(2)}
                            </td>
                          )}
                          {hsnSummary.isIGST && (
                            <td className="jt-hsn-td jt-hsn-col-igst text-end">
                              {(r.igst || 0).toFixed(2)}
                            </td>
                          )}
                          <td className="jt-hsn-td jt-hsn-col-total text-end">
                            {r.total.toFixed(2)}
                          </td>
                        </tr>
                      ))}
                      <tr className="jt-hsn-row jt-hsn-totalrow">
                        <td className="jt-hsn-td jt-hsn-col-hsn jt-bold">
                          TOTAL
                        </td>
                        <td className="jt-hsn-td jt-bold text-end">
                          {round2(hsnSummary.totals.taxable).toFixed(2)}
                        </td>
                        <td />
                        {!hsnSummary.isIGST && (
                          <td className="jt-hsn-td jt-bold text-end">
                            {round2(hsnSummary.totals.cgst).toFixed(2)}
                          </td>
                        )}
                        {!hsnSummary.isIGST && (
                          <td className="jt-hsn-td jt-bold text-end">
                            {round2(hsnSummary.totals.sgst).toFixed(2)}
                          </td>
                        )}
                        {hsnSummary.isIGST && (
                          <td className="jt-hsn-td jt-bold text-end">
                            {round2(hsnSummary.totals.igst).toFixed(2)}
                          </td>
                        )}
                        <td className="jt-hsn-td jt-bold text-end">
                          {round2(hsnSummary.totals.total).toFixed(2)}
                        </td>
                      </tr>
                    </tbody>
                  </Table>

                  {!consistencyOK && (
                    <div
                      style={{
                        color: "red",
                        fontSize: 12,
                        marginTop: 6,
                        fontWeight: 600,
                      }}
                    >
                      Totals mismatch detected. Auto-recalculating… Submit will
                      enable once values match.
                    </div>
                  )}
                </div>
              )}

              <div className="right-summary jt-right">
                <ul className="totals-ul jt-totals">
                  <li className="jt-total-row">
                    <span className="jt-total-label">Basic Total</span>
                    <strong className="jt-total-val">
                      {Number(sumLineBasics(product)).toFixed(2)}
                    </strong>
                  </li>
                  <li className="jt-total-row">
                    <span className="jt-total-label">Discount Total</span>
                    <strong className="jt-total-val">
                      {selectedDiscountRows
                        .reduce((a, r) => a + (Number(r.__applied) || 0), 0)
                        .toFixed(2)}
                    </strong>
                  </li>
                  <li className="jt-total-row">
                    <span className="jt-total-label">Charges Total</span>
                    <strong className="jt-total-val">
                      {selectedChargesRows
                        .reduce((a, r) => a + (Number(r.__applied) || 0), 0)
                        .toFixed(2)}
                    </strong>
                  </li>
                  <li className="jt-total-row">
                    <span className="jt-total-label">Taxable Value</span>
                    <strong className="jt-total-val">
                      {(finalPrice > 0
                        ? Number(finalPrice)
                        : Number(sumLineBasics(product))
                      ).toFixed(2)}
                    </strong>
                  </li>

                  {GSTData?.Tax?.IgstTaxType ? (
                    <li className="jt-total-row">
                      <span className="jt-total-label">
                        IGST (incl. on charges)
                      </span>
                      <strong className="jt-total-val">
                        {Number(
                          (
                            Number(GSTData?.Tax?.IgstTotal || 0) +
                            Number(taxRate || 0)
                          ).toFixed(2),
                        ).toFixed(2)}
                      </strong>
                    </li>
                  ) : (
                    <>
                      <li className="jt-total-row">
                        <span className="jt-total-label">
                          SGST (incl. on charges)
                        </span>
                        <strong className="jt-total-val">
                          {Number(
                            (
                              Number(GSTData?.Tax?.SgstTotal || 0) +
                              Number(taxRate || 0) / 2
                            ).toFixed(2),
                          ).toFixed(2)}
                        </strong>
                      </li>
                      <li className="jt-total-row">
                        <span className="jt-total-label">
                          CGST (incl. on charges)
                        </span>
                        <strong className="jt-total-val">
                          {Number(
                            (
                              Number(GSTData?.Tax?.CgstTotal || 0) +
                              Number(taxRate || 0) / 2
                            ).toFixed(2),
                          ).toFixed(2)}
                        </strong>
                      </li>
                    </>
                  )}

                  <li className="jt-total-row">
                    <span className="jt-total-label">RoundOff</span>
                    <strong className="jt-total-val">
                      {Number(localRoundOff || 0).toFixed(2)}
                    </strong>
                  </li>

                  <hr className="jt-hr" />
                  <li className="jt-total-row jt-grand">
                    <span className="jt-total-label">Grand Total</span>
                    <strong className="jt-total-val">
                      {Number(finalPriceAfterTax || 0).toFixed(2)}
                    </strong>
                  </li>
                </ul>

                <div className="d-flex justify-content-end jt-submit">
                  <Button
                    color={
                      hasQtyError
                        ? "secondary"
                        : !consistencyOK
                          ? "secondary"
                          : borderColor !== "red" ||
                              emergencyStatus === "Active"
                            ? "primary"
                            : "secondary"
                    }
                    type="submit"
                    className="mt-2 jt-submit-btn"
                    disabled={
                      Loading ||
                      hasQtyError ||
                      !consistencyOK ||
                      (borderColor === "red" && emergencyStatus !== "Active")
                    }
                  >
                    {hasQtyError
                      ? "Qty not available"
                      : !consistencyOK
                        ? "Recalculating…"
                        : borderColor === "red" && emergencyStatus !== "Active"
                          ? "Limit exceeded"
                          : Loading
                            ? "Submitting..."
                            : "Create Order"}
                  </Button>
                </div>
              </div>
            </div>
          </Form>
        </CardBody>
      </Card>

      <Modal isOpen={remarkModel} toggle={toggleRemarkModal}>
        <ModalHeader toggle={toggleRemarkModal}>
          Enter Emergency Remark
        </ModalHeader>
        <ModalBody>
          <Label htmlFor="remark">Emergency Message</Label>
          <Input
            type="textarea"
            id="remark"
            placeholder="Enter your emergency message..."
            value={remarkMessage}
            onChange={(e) => setRemarkMessage(e.target.value)}
          />
          <div className="d-flex justify-content-end mt-2">
            <Button color="secondary" onClick={toggleRemarkModal}>
              Cancel
            </Button>
            <Button
              color="primary"
              className="ms-2"
              onClick={async () => {
                const userData = JSON.parse(localStorage.getItem("userData"));
                const financialYearSelected = localStorage.getItem(
                  "financialYearSelected",
                );
                const selectedDatabase = `${userData?.database}-${financialYearSelected}`;
                const Payload = {
                  partyId: Party?._id ? Party?._id : PartyId,
                  count: 0,
                  database: selectedDatabase,
                  status: "Pending",
                  remark: remarkMessage,
                };
                const res = await _PostSave(EmergencyInvoice, Payload);
                if (res?.status) {
                  swal("Request for emergency order submitted");
                  setRemarkModel(false);
                  setRemarkMessage("");
                  setEmergencyInvoice(true);
                  setEmergencyStatus("Pending");
                } else {
                  swal("Error in emergency order");
                }
              }}
            >
              Submit
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </div>
  );
};

export default CreateOrder;
